import React, { useState, useEffect, useMemo, useCallback, useRef, type SetStateAction } from 'react';
import { Droplets, Check, X, AlertTriangle, Clock, User, Heart, Copy, ExternalLink, Shield, Sparkles, Gift, Wallet, Send, Loader2, Activity, Search, Ban, TrendingUp, FileWarning, ChevronDown, ChevronUp, Zap, BarChart3, Users } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { getReadRelays } from '../hooks/useNostrPublish';
import { useBCHAddress } from '../hooks/useBCHAddress';
import {
  FAUCET_CLAIM_KIND, FAUCET_D_TAG, FAUCET_TAG, FAUCET_TIERS, getRewardTier,
  LOYALTY_D_TAG, LOYALTY_TAG, LOYALTY_CLAIM_KIND, LOYALTY_PERIOD_DAYS,
  LOYALTY_MIN_TIP_SATS, LOYALTY_MIN_TIPS_REQUIRED, LOYALTY_MIN_POSTS_REQUIRED,
  LOYALTY_TIERS, getLoyaltyTier,
} from '../lib/faucet';
import { OPERATOR_PUBKEY } from '../lib/platform';
import { useBCHPrice } from '../hooks/useBCHPrice';
import {
  verifyClaimantTips, describeTipVerification, looksFabricated,
  type TipVerificationSummary,
} from '../lib/tipVerify';
import { verifyTxPaysAddress } from '../lib/paymentAudit';

import { Link } from 'react-router-dom';
import { timeAgo } from '../lib/utils';
import { useTipWallet } from '../hooks/useTipWallet';
import { isValidBCHAddress, normalizeBCHAddress } from '../hooks/useBCHAddress';

function satsToUSD(sats: number, bchPrice: number | null): string | null {
  if (!bchPrice || bchPrice <= 0) return null;
  const usd = (sats / 1e8) * bchPrice;
  if (usd < 0.01) return '<$0.01';
  return '$' + usd.toFixed(2);
}

export default function FaucetPage() {
  const { user, login } = useAppContext();
  const { publish } = useNostrPublish();
  const { address: myBCHAddress } = useBCHAddress(user?.pubkey);
  const { profile } = useNostrProfile(user?.pubkey);
  const { price: bchPrice } = useBCHPrice();
  const paidClaims = usePaidClaims();
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [claimSuccess, setClaimSuccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const isOperator = user?.pubkey === OPERATOR_PUBKEY;

  // Check if user already claimed (kind 30078 with d-tag)
  const { events: myClaimEvents } = useNostrQuery({
    filters: user ? [{ kinds: [FAUCET_CLAIM_KIND], authors: [user.pubkey], '#d': [FAUCET_D_TAG], limit: 1 }] : [],
    enabled: !!user,
  });
  // Daily claim: check if claimed in last 24 hours (not lifetime)
  const alreadyClaimedToday = myClaimEvents.length > 0 && (Math.floor(Date.now() / 1000) - myClaimEvents[0].created_at) < 86400;
  const totalClaims = myClaimEvents.length;

  // Fetch user's tip count (kind 9735 with #bch-tip)
  const [tipSince] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 90); // 90 days
  const { events: myTips } = useNostrQuery({
    filters: user ? [{ kinds: [9735], authors: [user.pubkey], '#t': ['bch-tip'], limit: 100, since: tipSince }] : [],
    enabled: !!user,
  });
  const tipCount = myTips.length;

  // Count qualifying tips: must be $0.05+ (min ~125 sats at $400/BCH, use 100 sats as safe floor)
  const MIN_TIP_SATS = 100; // ~$0.05 at current BCH prices
  const TXID_RE = /^[0-9a-f]{64}$/;
  const qualifyingTips = useMemo(() => {
    return myTips.filter(ev => {
      const pTag = ev.tags.find((t: string[]) => t[0] === 'p');
      const amountTag = ev.tags.find((t: string[]) => t[0] === 'amount');
      const amount = amountTag ? parseInt(amountTag[1]) : 0;
      // Must carry a real on-chain txid. A tip receipt is self-published JSON,
      // so without this a user could mint fake receipts (no BCH sent) to farm
      // faucet rewards. Genuine tips from BCHTipButton always record the txid;
      // the operator payout screen additionally verifies these on-chain.
      let txid = '';
      try {
        const c = JSON.parse(ev.content) as { txid?: unknown };
        if (typeof c.txid === 'string') txid = c.txid.trim().toLowerCase();
      } catch { /* no content */ }
      if (!TXID_RE.test(txid)) {
        const feeTag = ev.tags.find((t: string[]) => t[0] === 'fee')?.[1] || '';
        txid = feeTag.toLowerCase();
      }
      const hasTxid = TXID_RE.test(txid);
      // Must be to a different user, at least $0.05 worth, and carry a txid.
      return pTag && pTag[1] !== user?.pubkey && amount >= MIN_TIP_SATS && hasTxid;
    });
  }, [myTips, user?.pubkey]);

  const qualifyingTipCount = qualifyingTips.length;

  // Count unique users tipped (only qualifying tips)
  const uniqueUsersTipped = useMemo(() => {
    const recipients = new Set<string>();
    qualifyingTips.forEach(ev => {
      const pTag = ev.tags.find((t: string[]) => t[0] === 'p');
      if (pTag) recipients.add(pTag[1]);
    });
    return recipients.size;
  }, [qualifyingTips]);

  const rewardTier = getRewardTier(qualifyingTipCount);

  // One claim per npub ever
  const alreadyClaimed = myClaimEvents.length > 0;

  // Check eligibility
  const hasProfile = !!(profile?.name && profile?.picture);
  const hasBCHAddress = !!myBCHAddress;
  const tippedEnoughUsers = uniqueUsersTipped >= 3;
  const isEligible = hasProfile && hasBCHAddress && tippedEnoughUsers && !alreadyClaimed;

  const eligibilityChecks = [
    { label: 'Profile name & picture set', passed: hasProfile, detail: hasProfile ? 'Complete' : 'Go to Settings > Profile' },
    { label: 'BCH address set', passed: hasBCHAddress, detail: hasBCHAddress ? myBCHAddress!.replace('bitcoincash:', '').slice(0, 12) + '...' : 'Set in Settings > Profile' },
    { label: 'Tipped 3+ different users ($0.05+ each)', passed: tippedEnoughUsers, detail: `${uniqueUsersTipped} unique user${uniqueUsersTipped !== 1 ? 's' : ''} tipped${uniqueUsersTipped < 3 ? ' (need 3)' : ''}` },
    { label: 'Not already claimed', passed: !alreadyClaimed, detail: alreadyClaimed ? 'Already claimed — one per account' : 'Ready' },
  ];

  // Submit claim
  const handleClaim = async () => {
    if (!user || !isEligible || !rewardTier) return;
    setClaiming(true);
    setClaimError(null);
    try {
      await publish({
        kind: FAUCET_CLAIM_KIND,
        content: JSON.stringify({
          reward: rewardTier.reward,
          tier: rewardTier.label,
          tipCount,
          bchAddress: myBCHAddress,
          timestamp: Math.floor(Date.now() / 1000),
        }),
        tags: [
          ['d', FAUCET_D_TAG],
          ['t', FAUCET_TAG],
          ['t', 'bch'],
          ['amount', String(rewardTier.reward)],
          ['bch_address', myBCHAddress || ''],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });
      setClaimSuccess(true);
    } catch (e) {
      setClaimError(e instanceof Error ? e.message : 'Claim failed');
    } finally { setClaiming(false); }
  };

  // Fetch all claims (for operator view + stats)
  const { events: allClaims, loading: claimsLoading } = useNostrQuery({
    filters: [{ kinds: [FAUCET_CLAIM_KIND], '#d': [FAUCET_D_TAG], limit: 200 }],
  });

  const totalClaimed = allClaims.reduce((sum, ev) => {
    try { return sum + (JSON.parse(ev.content).reward || 0); } catch { return sum; }
  }, 0);

  return (
    <div>
      <div className="sticky top-0 lg:top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <h1 className="text-lg font-bold flex items-center gap-2">
          <Droplets className="w-5 h-5 text-blue-500" /> BCH Faucet
        </h1>
        <p className="text-xs text-muted-foreground mt-0.5">Free BCH for active BCHNostr community members</p>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-4">
        {/* Hero */}
        <div className="glass-card overflow-hidden text-center">
          <div className="bg-gradient-to-r from-blue-500/10 via-primary/10 to-blue-500/10 -m-4 mb-4 p-6">
            <Droplets className="w-10 h-10 text-blue-500 mx-auto mb-2" />
            <h2 className="text-xl font-bold">Get Free BCH</h2>
            <p className="text-sm text-muted-foreground mt-1">Rewards based on your tipping activity</p>
          </div>

          {/* Reward tiers */}
          <div className="space-y-2 mb-4">
            {FAUCET_TIERS.map(tier => {
              const usd = satsToUSD(tier.reward, bchPrice);
              return (
                <div key={tier.label} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border ${rewardTier?.label === tier.label ? 'border-primary bg-primary/5' : 'border-border'}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{tier.emoji}</span>
                    <div>
                      <p className="text-xs font-semibold">{tier.label}</p>
                      <p className="text-[10px] text-muted-foreground">{tier.minTips}+ tips sent</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="font-bold font-mono text-sm">{usd || `${tier.reward.toLocaleString()} sats`}</span>
                    {usd && <p className="text-[10px] text-muted-foreground font-mono">{tier.reward.toLocaleString()} sats</p>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="rounded-lg bg-muted/50 p-2.5 text-center">
              <p className="text-lg font-bold">{allClaims.length}</p>
              <p className="text-[10px] text-muted-foreground">Claims</p>
            </div>
            <div className="rounded-lg bg-muted/50 p-2.5 text-center">
              <p className="text-lg font-bold">{satsToUSD(totalClaimed, bchPrice) || totalClaimed.toLocaleString()}</p>
              <p className="text-[10px] text-muted-foreground">{satsToUSD(totalClaimed, bchPrice) ? `${totalClaimed.toLocaleString()} sats distributed` : 'Sats distributed'}</p>
            </div>
          </div>
        </div>

        {/* Eligibility */}
        {user && (
          <div className="glass-card">
            <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-primary" /> Eligibility Check
            </h3>
            <div className="space-y-2">
              {eligibilityChecks.map(check => (
                <div key={check.label} className="flex items-center gap-2.5">
                  {check.passed ? (
                    <div className="w-5 h-5 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-emerald-500" /></div>
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-red-500/10 flex items-center justify-center shrink-0"><X className="w-3 h-3 text-red-500" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs ${check.passed ? 'text-foreground' : 'text-muted-foreground'}`}>{check.label}</p>
                    <p className="text-[10px] text-muted-foreground">{check.detail}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Your tier */}
            {rewardTier && !alreadyClaimed && (
              <div className="mt-4 rounded-lg bg-primary/5 border border-primary/20 p-3 text-center">
                <p className="text-sm font-bold flex items-center justify-center gap-1.5">
                  {rewardTier.emoji} {rewardTier.label} — {satsToUSD(rewardTier.reward, bchPrice) || `${rewardTier.reward.toLocaleString()} sats`}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {satsToUSD(rewardTier.reward, bchPrice) && `${rewardTier.reward.toLocaleString()} sats · `}Based on your {qualifyingTipCount} qualifying tip{qualifyingTipCount !== 1 ? 's' : ''} ($0.05+)
                </p>
              </div>
            )}

            {/* Claim button */}
            {claimSuccess ? (
              <div className="mt-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-4 text-center space-y-2">
                <Sparkles className="w-8 h-8 text-emerald-500 mx-auto" />
                <p className="text-sm font-bold text-emerald-500">Claim Submitted!</p>
                <p className="text-xs text-muted-foreground">Your claim for {satsToUSD(rewardTier?.reward || 0, bchPrice) || `${rewardTier?.reward.toLocaleString()} sats`} has been recorded. The operator will send BCH to your address.</p>
                {myBCHAddress && <p className="text-[10px] font-mono text-muted-foreground">{myBCHAddress.replace('bitcoincash:', '').slice(0, 20)}...</p>}
              </div>
            ) : alreadyClaimed ? (
              <div className="mt-4 rounded-lg bg-muted p-3 text-center space-y-1">
                <p className="text-xs text-muted-foreground flex items-center justify-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /> Faucet claimed!</p>
                <p className="text-[10px] text-muted-foreground">One claim per account. Thank you for being part of the community!</p>
              </div>
            ) : (
              <button onClick={isEligible ? handleClaim : undefined} disabled={!isEligible || claiming}
                className={`w-full mt-4 py-3 rounded-lg font-bold text-sm flex items-center justify-center gap-2 ${
                  isEligible ? 'bg-gradient-to-r from-blue-500 to-primary text-white hover:opacity-90' : 'bg-muted text-muted-foreground cursor-not-allowed'
                } disabled:opacity-50`}
              >
                {claiming ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Droplets className="w-4 h-4" />}
                {claiming ? 'Submitting...' : isEligible ? `Claim ${satsToUSD(rewardTier?.reward || 0, bchPrice) || `${rewardTier?.reward.toLocaleString()} sats`}` : 'Complete requirements above'}
              </button>
            )}
            {claimError && <p className="text-xs text-red-500 mt-2">{claimError}</p>}
          </div>
        )}

        {!user && (
          <div className="glass-card text-center py-6">
            <Droplets className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground mb-3">Log in to check your eligibility</p>
            <button onClick={login} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium">Log In</button>
          </div>
        )}

        {/* Loyalty Rewards — biweekly recurring */}
        {user && <LoyaltySection user={user} publish={publish} myBCHAddress={myBCHAddress} bchPrice={bchPrice} />}

        {/* How to qualify */}
        <div className="glass-card">
          <h3 className="font-semibold text-sm flex items-center gap-2 mb-3"><Gift className="w-4 h-4 text-primary" /> How to Qualify</h3>
          <div className="space-y-2 text-xs text-muted-foreground">
            <p><strong className="text-foreground">1. Set up your profile.</strong> Add a name and profile picture in Settings.</p>
            <p><strong className="text-foreground">2. Set your BCH address.</strong> So we know where to send your BCH.</p>
            <p><strong className="text-foreground">3. Tip 3+ different users ($0.05+ each).</strong> No self-tipping. Tips under $0.05 don't count. The more you tip, the bigger your reward.</p>
            <p><strong className="text-foreground">4. Claim once.</strong> One claim per account, forever. Make it count!</p>
          </div>
        </div>

        {/* Operator: Diagnostics (claims, fraud detection, mass payout) */}
        {isOperator && allClaims.length > 0 && (
          <DiagnosticsPanel
            onboardingClaims={allClaims}
            bchPrice={bchPrice}
            paidClaims={paidClaims}
          />
        )}
      </div>
    </div>
  );
}

// ── Loyalty Rewards (biweekly, recurring) ────────────────────────
function LoyaltySection({ user, publish, myBCHAddress, bchPrice }: { user: any; publish: any; myBCHAddress: string | null; bchPrice: number | null }) {
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [claimSuccess, setClaimSuccess] = useState(false);

  const now = Math.floor(Date.now() / 1000);
  const periodStart = now - LOYALTY_PERIOD_DAYS * 86400;

  // Check last loyalty claim
  const { events: myLoyaltyClaims } = useNostrQuery({
    filters: [{ kinds: [LOYALTY_CLAIM_KIND], authors: [user.pubkey], '#d': [LOYALTY_D_TAG], limit: 1 }],
    enabled: true,
  });

  const lastClaim = myLoyaltyClaims.length > 0 ? myLoyaltyClaims[0] : null;
  const canClaimAgain = !lastClaim || (now - lastClaim.created_at) >= LOYALTY_PERIOD_DAYS * 86400;
  const nextClaimTime = lastClaim ? lastClaim.created_at + LOYALTY_PERIOD_DAYS * 86400 : 0;
  const daysUntilClaim = canClaimAgain ? 0 : Math.ceil((nextClaimTime - now) / 86400);

  // Fetch tips in this period (qualifying: $0.05+, different users)
  const { events: recentTips } = useNostrQuery({
    filters: [{ kinds: [9735], authors: [user.pubkey], '#t': ['bch-tip'], since: periodStart, limit: 100 }],
    enabled: true,
  });

  const qualifyingRecentTips = useMemo(() => {
    const uniqueRecipients = new Set<string>();
    return recentTips.filter(ev => {
      const pTag = ev.tags.find((t: string[]) => t[0] === 'p');
      const amountTag = ev.tags.find((t: string[]) => t[0] === 'amount');
      const amount = amountTag ? parseInt(amountTag[1]) : 0;
      if (pTag && pTag[1] !== user.pubkey && amount >= LOYALTY_MIN_TIP_SATS) {
        uniqueRecipients.add(pTag[1]);
        return true;
      }
      return false;
    });
  }, [recentTips, user.pubkey]);

  const uniqueRecentRecipients = useMemo(() => {
    const s = new Set<string>();
    qualifyingRecentTips.forEach(ev => {
      const pTag = ev.tags.find((t: string[]) => t[0] === 'p');
      if (pTag) s.add(pTag[1]);
    });
    return s.size;
  }, [qualifyingRecentTips]);

  // Fetch posts in this period
  const { events: recentPosts } = useNostrQuery({
    filters: [{ kinds: [1], authors: [user.pubkey], since: periodStart, limit: 10 }],
    enabled: true,
  });

  const loyaltyTier = getLoyaltyTier(uniqueRecentRecipients);
  const meetsRequirements = uniqueRecentRecipients >= LOYALTY_MIN_TIPS_REQUIRED && recentPosts.length >= LOYALTY_MIN_POSTS_REQUIRED && !!myBCHAddress;
  const isEligible = meetsRequirements && canClaimAgain && !!loyaltyTier;

  const handleLoyaltyClaim = async () => {
    if (!isEligible || !loyaltyTier) return;
    setClaiming(true);
    setClaimError(null);
    try {
      await publish({
        kind: LOYALTY_CLAIM_KIND,
        content: JSON.stringify({
          reward: loyaltyTier.reward,
          tier: loyaltyTier.label,
          uniqueRecipients: uniqueRecentRecipients,
          posts: recentPosts.length,
          bchAddress: myBCHAddress,
          timestamp: now,
        }),
        tags: [
          ['d', LOYALTY_D_TAG],
          ['t', LOYALTY_TAG],
          ['t', 'bch'],
          ['amount', String(loyaltyTier.reward)],
          ['bch_address', myBCHAddress || ''],
        ],
        created_at: now,
      });
      setClaimSuccess(true);
    } catch (e) {
      setClaimError(e instanceof Error ? e.message : 'Claim failed');
    } finally { setClaiming(false); }
  };

  return (
    <div className="glass-card">
      <h3 className="font-semibold text-sm flex items-center gap-2 mb-3">
        <Heart className="w-4 h-4 text-pink-500" /> Loyalty Rewards
        <span className="text-[10px] bg-pink-500/10 text-pink-500 px-1.5 py-0.5 rounded-full font-medium">Every {LOYALTY_PERIOD_DAYS} days</span>
      </h3>

      <p className="text-xs text-muted-foreground mb-3">
        Stay active and earn recurring BCH rewards. Tip at least {LOYALTY_MIN_TIPS_REQUIRED} unique users and post {LOYALTY_MIN_POSTS_REQUIRED}+ note{LOYALTY_MIN_POSTS_REQUIRED > 1 ? 's' : ''} every {LOYALTY_PERIOD_DAYS} days.
      </p>

      {/* Loyalty tiers */}
      <div className="space-y-2 mb-4">
        {LOYALTY_TIERS.map(tier => {
          const usd = satsToUSD(tier.reward, bchPrice);
          return (
            <div key={tier.label} className={`flex items-center justify-between px-3 py-2 rounded-lg border ${loyaltyTier?.label === tier.label ? 'border-pink-500 bg-pink-500/5' : 'border-border'}`}>
              <div className="flex items-center gap-2">
                <span className="text-lg">{tier.emoji}</span>
                <div>
                  <p className="text-xs font-semibold">{tier.label}</p>
                  <p className="text-[10px] text-muted-foreground">{tier.minTips}+ unique users tipped</p>
                </div>
              </div>
              <div className="text-right">
                <span className="font-bold font-mono text-sm">{usd || `${tier.reward.toLocaleString()} sats`}</span>
                {usd && <p className="text-[10px] text-muted-foreground font-mono">{tier.reward.toLocaleString()} sats</p>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress checklist */}
      <div className="space-y-1.5 mb-4">
        <div className="flex items-center gap-2">
          {uniqueRecentRecipients >= LOYALTY_MIN_TIPS_REQUIRED ? (
            <div className="w-4 h-4 rounded-full bg-emerald-500/10 flex items-center justify-center"><Check className="w-2.5 h-2.5 text-emerald-500" /></div>
          ) : (
            <div className="w-4 h-4 rounded-full bg-muted flex items-center justify-center"><X className="w-2.5 h-2.5 text-muted-foreground" /></div>
          )}
          <p className="text-xs text-muted-foreground">{uniqueRecentRecipients}/{LOYALTY_MIN_TIPS_REQUIRED} unique users tipped this period</p>
        </div>
        <div className="flex items-center gap-2">
          {recentPosts.length >= LOYALTY_MIN_POSTS_REQUIRED ? (
            <div className="w-4 h-4 rounded-full bg-emerald-500/10 flex items-center justify-center"><Check className="w-2.5 h-2.5 text-emerald-500" /></div>
          ) : (
            <div className="w-4 h-4 rounded-full bg-muted flex items-center justify-center"><X className="w-2.5 h-2.5 text-muted-foreground" /></div>
          )}
          <p className="text-xs text-muted-foreground">{recentPosts.length}/{LOYALTY_MIN_POSTS_REQUIRED} post{LOYALTY_MIN_POSTS_REQUIRED > 1 ? 's' : ''} this period</p>
        </div>
        <div className="flex items-center gap-2">
          {myBCHAddress ? (
            <div className="w-4 h-4 rounded-full bg-emerald-500/10 flex items-center justify-center"><Check className="w-2.5 h-2.5 text-emerald-500" /></div>
          ) : (
            <div className="w-4 h-4 rounded-full bg-muted flex items-center justify-center"><X className="w-2.5 h-2.5 text-muted-foreground" /></div>
          )}
          <p className="text-xs text-muted-foreground">BCH address set</p>
        </div>
        <div className="flex items-center gap-2">
          {canClaimAgain ? (
            <div className="w-4 h-4 rounded-full bg-emerald-500/10 flex items-center justify-center"><Check className="w-2.5 h-2.5 text-emerald-500" /></div>
          ) : (
            <div className="w-4 h-4 rounded-full bg-amber-500/10 flex items-center justify-center"><Clock className="w-2.5 h-2.5 text-amber-500" /></div>
          )}
          <p className="text-xs text-muted-foreground">
            {canClaimAgain ? 'Cooldown ready' : `${daysUntilClaim} day${daysUntilClaim !== 1 ? 's' : ''} until next claim`}
          </p>
        </div>
      </div>

      {/* Claim button */}
      {claimSuccess ? (
        <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-3 text-center space-y-1">
          <Sparkles className="w-6 h-6 text-emerald-500 mx-auto" />
          <p className="text-xs font-bold text-emerald-500">Loyalty Reward Claimed!</p>
          <p className="text-[10px] text-muted-foreground">{satsToUSD(loyaltyTier?.reward || 0, bchPrice) || `${loyaltyTier?.reward.toLocaleString()} sats`} will be sent to your address.</p>
        </div>
      ) : (
        <button onClick={isEligible ? handleLoyaltyClaim : undefined} disabled={!isEligible || claiming}
          className={`w-full py-2.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2 ${
            isEligible ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:opacity-90' : 'bg-muted text-muted-foreground cursor-not-allowed'
          } disabled:opacity-50`}
        >
          {claiming ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Heart className="w-4 h-4" />}
          {claiming ? 'Submitting...' : isEligible ? `Claim ${satsToUSD(loyaltyTier?.reward || 0, bchPrice) || `${loyaltyTier?.reward.toLocaleString()} sats`}` : !canClaimAgain ? `Available in ${daysUntilClaim} days` : 'Complete requirements above'}
        </button>
      )}
      {claimError && <p className="text-xs text-red-500 mt-2">{claimError}</p>}

      {/* Recent loyalty claimers */}
      <LoyaltyClaimers />
    </div>
  );
}

function LoyaltyClaimers() {
  const { events: allLoyaltyClaims } = useNostrQuery({
    filters: [{ kinds: [LOYALTY_CLAIM_KIND], '#d': [LOYALTY_D_TAG], limit: 50 }],
    enabled: true,
  });

  // Deduplicate by pubkey (keep most recent claim per user)
  const uniqueClaimers = useMemo(() => {
    const seen = new Map<string, any>();
    allLoyaltyClaims
      .sort((a, b) => b.created_at - a.created_at)
      .forEach(ev => { if (!seen.has(ev.pubkey)) seen.set(ev.pubkey, ev); });
    return Array.from(seen.values());
  }, [allLoyaltyClaims]);

  if (uniqueClaimers.length === 0) return null;

  return (
    <div className="mt-4 pt-4 border-t border-border">
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5">
        <Check className="w-3 h-3 text-emerald-500" /> Claimed this period ({uniqueClaimers.length})
      </p>
      <div className="flex flex-wrap gap-1.5">
        {uniqueClaimers.map(ev => (
          <LoyaltyClaimerAvatar key={ev.pubkey} pubkey={ev.pubkey} />
        ))}
      </div>
    </div>
  );
}

function LoyaltyClaimerAvatar({ pubkey }: { pubkey: string }) {
  const { profile } = useNostrProfile(pubkey);
  return (
    <Link to={`/profile/${pubkey}`} className="relative group" title={profile?.name || pubkey.slice(0, 8)}>
      {profile?.picture ? (
        <img src={profile.picture} alt="" className="w-8 h-8 rounded-full object-cover border-2 border-emerald-500/30 group-hover:border-emerald-500 transition-colors" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-emerald-500/10 border-2 border-emerald-500/30 flex items-center justify-center">
          <User className="w-4 h-4 text-emerald-500/60" />
        </div>
      )}
      <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 flex items-center justify-center border border-background">
        <Check className="w-2 h-2 text-white" />
      </div>
    </Link>
  );
}

// ── Paid Claims — synced to Nostr (kind 30078, NIP-44 encrypted) ──
// Tracks BOTH event IDs and pubkeys so we recognize returning users
const PAID_CLAIMS_D_TAG = 'bchnostr/paid-claims-v2';
const PAID_CLAIMS_LOCAL_KEY = 'bchnostr:paid-claims-v2';

/** Per-claim payout receipt — the on-chain proof a claim was actually paid. */
interface PayoutReceipt {
  txid: string;      // the batch transaction that paid this claim
  address: string;   // the exact address it was paid to (may differ from the
                     // claimant's CURRENT address if they changed it later)
  sats: number;      // amount attributed to this claim
  paidAt: number;    // unix seconds when recorded
}

interface PaidClaimsData {
  eventIds: string[];   // All event IDs marked as paid
  pubkeys: string[];    // All pubkeys that have ever been paid
  // eventId → payout receipt. Optional/back-compat: older data has none, which
  // is exactly how we detect "marked paid but with no on-chain proof".
  payouts?: Record<string, PayoutReceipt>;
}

// Local cache (immediate reads)
function getPaidClaimsLocal(): PaidClaimsData {
  try {
    const raw = localStorage.getItem(PAID_CLAIMS_LOCAL_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Handle migration from old format (plain array of event IDs)
      if (Array.isArray(parsed)) {
        return { eventIds: parsed, pubkeys: [], payouts: {} };
      }
      return {
        eventIds: parsed.eventIds || [],
        pubkeys: parsed.pubkeys || [],
        payouts: parsed.payouts || {},
      };
    }
    // Migrate from old v1 key
    const oldRaw = localStorage.getItem('bchnostr:paid-claims');
    if (oldRaw) {
      const oldArr = JSON.parse(oldRaw);
      if (Array.isArray(oldArr)) {
        const migrated = { eventIds: oldArr, pubkeys: [], payouts: {} };
        savePaidClaimsLocal(migrated);
        return migrated;
      }
    }
    return { eventIds: [], pubkeys: [], payouts: {} };
  } catch { return { eventIds: [], pubkeys: [], payouts: {} }; }
}

function savePaidClaimsLocal(data: PaidClaimsData) {
  try { localStorage.setItem(PAID_CLAIMS_LOCAL_KEY, JSON.stringify(data)); } catch {}
}

// Publish paid claims to Nostr (encrypted, only readable by operator)
async function publishPaidClaims(data: PaidClaimsData) {
  try {
    const signer = (window as any).nostr;
    if (!signer?.nip44?.encrypt) return;
    const pubkey = await signer.getPublicKey();
    const plaintext = JSON.stringify(data);
    const encrypted = await signer.nip44.encrypt(pubkey, plaintext);
    const unsigned = {
      kind: 30078,
      content: encrypted,
      tags: [
        ['d', PAID_CLAIMS_D_TAG],
        ['alt', 'Encrypted paid claims tracker v2'],
      ],
      created_at: Math.floor(Date.now() / 1000),
    };
    const signed = await signer.signEvent(unsigned);
    const relays = getReadRelays().slice(0, 4);
    await Promise.allSettled(
      relays.map(url => new Promise<void>(resolve => {
        try {
          const ws = new WebSocket(url);
          ws.onopen = () => { ws.send(JSON.stringify(['EVENT', signed])); setTimeout(() => { ws.close(); resolve(); }, 2000); };
          ws.onerror = () => resolve();
          setTimeout(() => { try { ws.close(); } catch {} resolve(); }, 5000);
        } catch { resolve(); }
      }))
    );
  } catch (err) {
    console.warn('Failed to sync paid claims to Nostr:', err);
  }
}

// Fetch paid claims from Nostr
async function fetchPaidClaimsFromNostr(): Promise<PaidClaimsData | null> {
  try {
    const signer = (window as any).nostr;
    if (!signer?.nip44?.decrypt) return null;
    const pubkey = await signer.getPublicKey();
    const relays = getReadRelays().slice(0, 4);
    let bestEvent: any = null;

    await Promise.allSettled(
      relays.map(url => new Promise<void>(resolve => {
        try {
          const ws = new WebSocket(url);
          const subId = 'paid_' + Math.random().toString(36).slice(2, 8);
          ws.onopen = () => {
            ws.send(JSON.stringify(['REQ', subId, {
              kinds: [30078], authors: [pubkey], '#d': [PAID_CLAIMS_D_TAG], limit: 1,
            }]));
          };
          ws.onmessage = (msg) => {
            try {
              const data = JSON.parse(msg.data);
              if (data[0] === 'EVENT' && data[1] === subId) {
                if (!bestEvent || data[2].created_at > bestEvent.created_at) bestEvent = data[2];
              }
            } catch {}
          };
          setTimeout(() => { try { ws.close(); } catch {} resolve(); }, 6000);
        } catch { resolve(); }
      }))
    );

    if (!bestEvent?.content) return null;
    const decrypted = await signer.nip44.decrypt(pubkey, bestEvent.content);
    const parsed = JSON.parse(decrypted);
    // Handle old format (plain array)
    if (Array.isArray(parsed)) {
      return { eventIds: parsed, pubkeys: [], payouts: {} };
    }
    return {
      eventIds: parsed.eventIds || [],
      pubkeys: parsed.pubkeys || [],
      payouts: parsed.payouts || {},
    };
  } catch {
    return null;
  }
}

// Debounce timer for publishing
let publishTimer: any = null;

// Hook to manage paid claims with Nostr sync
function usePaidClaims() {
  const [paidData, setPaidData] = useState<PaidClaimsData>(getPaidClaimsLocal);
  const [synced, setSynced] = useState(false);
  const syncRef = useRef(false);

  // Derived sets for fast lookup
  const paidSet = useMemo(() => new Set(paidData.eventIds), [paidData.eventIds]);
  const paidPubkeys = useMemo(() => new Set(paidData.pubkeys), [paidData.pubkeys]);
  const payouts = useMemo(() => paidData.payouts || {}, [paidData.payouts]);

  // On mount, fetch from Nostr and merge with local
  useEffect(() => {
    if (syncRef.current) return;
    syncRef.current = true;
    (async () => {
      const remote = await fetchPaidClaimsFromNostr();
      if (remote) {
        const local = getPaidClaimsLocal();
        const merged: PaidClaimsData = {
          eventIds: [...new Set([...local.eventIds, ...remote.eventIds])],
          pubkeys: [...new Set([...local.pubkeys, ...remote.pubkeys])],
          // Union of payout receipts; a receipt (with a txid) always wins over
          // a bare "paid" mark so on-chain proof is never lost in a merge.
          payouts: { ...(remote.payouts || {}), ...(local.payouts || {}) },
        };
        setPaidData(merged);
        savePaidClaimsLocal(merged);
      }
      setSynced(true);
    })();
  }, []);

  const markPaid = useCallback((eventId: string, pubkey: string, paid: boolean, receipt?: PayoutReceipt) => {
    setPaidData(prev => {
      let next: PaidClaimsData;
      const payouts = { ...(prev.payouts || {}) };
      if (paid) {
        if (receipt) payouts[eventId] = receipt;
        next = {
          eventIds: [...new Set([...prev.eventIds, eventId])],
          pubkeys: [...new Set([...prev.pubkeys, pubkey])],
          payouts,
        };
      } else {
        delete payouts[eventId];
        next = {
          eventIds: prev.eventIds.filter(id => id !== eventId),
          // Don't remove pubkey — once paid, always recognized
          pubkeys: prev.pubkeys,
          payouts,
        };
      }
      savePaidClaimsLocal(next);
      // Debounce publish to Nostr (wait 2s so batch marks get combined)
      if (publishTimer) clearTimeout(publishTimer);
      publishTimer = setTimeout(() => publishPaidClaims(next), 2000);
      return next;
    });
  }, []);

  const markAllPaid = useCallback((
    claims: { eventId: string; pubkey: string; receipt?: PayoutReceipt }[],
  ) => {
    setPaidData(prev => {
      const payouts = { ...(prev.payouts || {}) };
      for (const c of claims) if (c.receipt) payouts[c.eventId] = c.receipt;
      const next: PaidClaimsData = {
        eventIds: [...new Set([...prev.eventIds, ...claims.map(c => c.eventId)])],
        pubkeys: [...new Set([...prev.pubkeys, ...claims.map(c => c.pubkey)])],
        payouts,
      };
      savePaidClaimsLocal(next);
      if (publishTimer) clearTimeout(publishTimer);
      publishTimer = setTimeout(() => publishPaidClaims(next), 2000);
      return next;
    });
  }, []);

  return { paidSet, paidPubkeys, payouts, markPaid, markAllPaid, synced };
}

// ══════════════════════════════════════════════════════════════════
//  FRAUD BLOCKLIST — Operator-managed blocked addresses
// ══════════════════════════════════════════════════════════════════

const FRAUD_BLOCKLIST_LOCAL_KEY = 'bchnostr:fraud-blocklist';
const FRAUD_BLOCKLIST_D_TAG = 'bchnostr/fraud-blocklist';

interface FraudBlocklistData {
  addresses: string[];     // Blocked BCH addresses (normalized)
  pubkeys: string[];       // Blocked pubkeys
  notes: Record<string, string>; // Optional notes per address/pubkey
}

function getFraudBlocklistLocal(): FraudBlocklistData {
  try {
    const raw = localStorage.getItem(FRAUD_BLOCKLIST_LOCAL_KEY);
    if (raw) return JSON.parse(raw);
    return { addresses: [], pubkeys: [], notes: {} };
  } catch { return { addresses: [], pubkeys: [], notes: {} }; }
}

function saveFraudBlocklistLocal(data: FraudBlocklistData) {
  try { localStorage.setItem(FRAUD_BLOCKLIST_LOCAL_KEY, JSON.stringify(data)); } catch {}
}

async function publishFraudBlocklist(data: FraudBlocklistData) {
  try {
    const signer = (window as any).nostr;
    if (!signer?.nip44?.encrypt) return;
    const pubkey = await signer.getPublicKey();
    const plaintext = JSON.stringify(data);
    const encrypted = await signer.nip44.encrypt(pubkey, plaintext);
    const unsigned = {
      kind: 30078,
      content: encrypted,
      tags: [
        ['d', FRAUD_BLOCKLIST_D_TAG],
        ['alt', 'Encrypted fraud blocklist'],
      ],
      created_at: Math.floor(Date.now() / 1000),
    };
    const signed = await signer.signEvent(unsigned);
    const relays = getReadRelays().slice(0, 4);
    await Promise.allSettled(
      relays.map(url => new Promise<void>(resolve => {
        try {
          const ws = new WebSocket(url);
          ws.onopen = () => { ws.send(JSON.stringify(['EVENT', signed])); setTimeout(() => { ws.close(); resolve(); }, 2000); };
          ws.onerror = () => resolve();
          setTimeout(() => { try { ws.close(); } catch {} resolve(); }, 5000);
        } catch { resolve(); }
      }))
    );
  } catch (err) {
    console.warn('Failed to sync fraud blocklist to Nostr:', err);
  }
}

async function fetchFraudBlocklistFromNostr(): Promise<FraudBlocklistData | null> {
  try {
    const signer = (window as any).nostr;
    if (!signer?.nip44?.decrypt) return null;
    const pubkey = await signer.getPublicKey();
    const relays = getReadRelays().slice(0, 4);
    let bestEvent: any = null;
    await Promise.allSettled(
      relays.map(url => new Promise<void>(resolve => {
        try {
          const ws = new WebSocket(url);
          const subId = 'fraud_' + Math.random().toString(36).slice(2, 8);
          ws.onopen = () => {
            ws.send(JSON.stringify(['REQ', subId, {
              kinds: [30078], authors: [pubkey], '#d': [FRAUD_BLOCKLIST_D_TAG], limit: 1,
            }]));
          };
          ws.onmessage = (msg) => {
            try {
              const data = JSON.parse(msg.data);
              if (data[0] === 'EVENT' && data[1] === subId) {
                if (!bestEvent || data[2].created_at > bestEvent.created_at) bestEvent = data[2];
              }
            } catch {}
          };
          setTimeout(() => { try { ws.close(); } catch {} resolve(); }, 6000);
        } catch { resolve(); }
      }))
    );
    if (!bestEvent?.content) return null;
    const decrypted = await signer.nip44.decrypt(pubkey, bestEvent.content);
    return JSON.parse(decrypted);
  } catch { return null; }
}

let fraudPublishTimer: any = null;

function useFraudBlocklist() {
  const [data, setData] = useState<FraudBlocklistData>(getFraudBlocklistLocal);
  const [synced, setSynced] = useState(false);
  const syncRef = useRef(false);

  const blockedAddresses = useMemo(() => new Set(data.addresses), [data.addresses]);
  const blockedPubkeys = useMemo(() => new Set(data.pubkeys), [data.pubkeys]);

  useEffect(() => {
    if (syncRef.current) return;
    syncRef.current = true;
    (async () => {
      const remote = await fetchFraudBlocklistFromNostr();
      if (remote) {
        const local = getFraudBlocklistLocal();
        const merged: FraudBlocklistData = {
          addresses: [...new Set([...local.addresses, ...remote.addresses])],
          pubkeys: [...new Set([...local.pubkeys, ...remote.pubkeys])],
          notes: { ...remote.notes, ...local.notes },
        };
        setData(merged);
        saveFraudBlocklistLocal(merged);
      }
      setSynced(true);
    })();
  }, []);

  const blockAddress = useCallback((addr: string, note?: string) => {
    const normalized = normalizeBCHAddress(addr) || addr;
    setData(prev => {
      const next: FraudBlocklistData = {
        addresses: [...new Set([...prev.addresses, normalized])],
        pubkeys: prev.pubkeys,
        notes: { ...prev.notes, ...(note ? { [normalized]: note } : {}) },
      };
      saveFraudBlocklistLocal(next);
      if (fraudPublishTimer) clearTimeout(fraudPublishTimer);
      fraudPublishTimer = setTimeout(() => publishFraudBlocklist(next), 2000);
      return next;
    });
  }, []);

  const unblockAddress = useCallback((addr: string) => {
    const normalized = normalizeBCHAddress(addr) || addr;
    setData(prev => {
      const notes = { ...prev.notes };
      delete notes[normalized];
      const next: FraudBlocklistData = {
        addresses: prev.addresses.filter(a => a !== normalized),
        pubkeys: prev.pubkeys,
        notes,
      };
      saveFraudBlocklistLocal(next);
      if (fraudPublishTimer) clearTimeout(fraudPublishTimer);
      fraudPublishTimer = setTimeout(() => publishFraudBlocklist(next), 2000);
      return next;
    });
  }, []);

  const blockPubkey = useCallback((pubkey: string, note?: string) => {
    setData(prev => {
      const next: FraudBlocklistData = {
        addresses: prev.addresses,
        pubkeys: [...new Set([...prev.pubkeys, pubkey])],
        notes: { ...prev.notes, ...(note ? { [pubkey]: note } : {}) },
      };
      saveFraudBlocklistLocal(next);
      if (fraudPublishTimer) clearTimeout(fraudPublishTimer);
      fraudPublishTimer = setTimeout(() => publishFraudBlocklist(next), 2000);
      return next;
    });
  }, []);

  const unblockPubkey = useCallback((pubkey: string) => {
    setData(prev => {
      const notes = { ...prev.notes };
      delete notes[pubkey];
      const next: FraudBlocklistData = {
        addresses: prev.addresses,
        pubkeys: prev.pubkeys.filter(p => p !== pubkey),
        notes,
      };
      saveFraudBlocklistLocal(next);
      if (fraudPublishTimer) clearTimeout(fraudPublishTimer);
      fraudPublishTimer = setTimeout(() => publishFraudBlocklist(next), 2000);
      return next;
    });
  }, []);

  const isAddressBlocked = useCallback((addr: string) => {
    const normalized = normalizeBCHAddress(addr) || addr;
    return blockedAddresses.has(normalized);
  }, [blockedAddresses]);

  const isPubkeyBlocked = useCallback((pubkey: string) => {
    return blockedPubkeys.has(pubkey);
  }, [blockedPubkeys]);

  const getNote = useCallback((key: string) => {
    return data.notes[key] || data.notes[normalizeBCHAddress(key) || key] || '';
  }, [data.notes]);

  return {
    blockedAddresses, blockedPubkeys, data,
    blockAddress, unblockAddress, blockPubkey, unblockPubkey,
    isAddressBlocked, isPubkeyBlocked, getNote, synced,
  };
}

// ══════════════════════════════════════════════════════════════════
//  DIAGNOSTICS PANEL — Operator-only deep analysis
// ══════════════════════════════════════════════════════════════════

interface ClaimDiag {
  eventId: string;
  pubkey: string;
  addr: string;
  addrNormalized: string | null;
  addrValid: boolean;
  reward: number;
  tier: string;
  tipCount: number;
  createdAt: number;
  isPaid: boolean;
  claimType: 'onboarding' | 'loyalty';
  issues: string[];
  riskLevel: 'ok' | 'warn' | 'danger';
  isFraudBlocked: boolean;
}

function DiagnosticsPanel({
  onboardingClaims,
  bchPrice,
  paidClaims,
}: {
  onboardingClaims: any[];
  bchPrice: number | null;
  paidClaims: ReturnType<typeof usePaidClaims>;
}) {
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'claims' | 'addresses' | 'fraud' | 'payout'>('overview');
  const fraudBlocklist = useFraudBlocklist();

  // Fetch loyalty claims too for combined diagnostics
  const { events: loyaltyClaims } = useNostrQuery({
    filters: [{ kinds: [LOYALTY_CLAIM_KIND], '#d': [LOYALTY_D_TAG], limit: 200 }],
    enabled: expanded,
  });

  // Build a set of onboarding event IDs for fast lookup
  const onboardingIds = useMemo(() => new Set(onboardingClaims.map((ev: any) => ev.id)), [onboardingClaims]);

  // Pre-compute shared address map for sybil detection
  const sharedAddressSet = useMemo(() => {
    const combined = [...onboardingClaims, ...loyaltyClaims];
    const addrPubkeys = new Map<string, Set<string>>();
    combined.forEach((ev: any) => {
      let data: any = {};
      try { data = JSON.parse(ev.content); } catch {}
      const addr = ev.tags.find((t: string[]) => t[0] === 'bch_address')?.[1] || data.bchAddress || '';
      const normalized = normalizeBCHAddress(addr) || addr;
      if (!normalized) return;
      const set = addrPubkeys.get(normalized) || new Set<string>();
      set.add(ev.pubkey);
      addrPubkeys.set(normalized, set);
    });
    // Return set of addresses used by multiple different pubkeys
    const shared = new Set<string>();
    addrPubkeys.forEach((pubkeys, addr) => {
      if (pubkeys.size > 1) shared.add(addr);
    });
    return shared;
  }, [onboardingClaims, loyaltyClaims]);

  // Parse all claims into diagnostic records
  const allDiags = useMemo(() => {
    const combined = [...onboardingClaims, ...loyaltyClaims];

    // Pre-compute onboarding pubkey counts for duplicate detection
    const onboardingPubkeyCounts = new Map<string, number>();
    onboardingClaims.forEach((ev: any) => {
      onboardingPubkeyCounts.set(ev.pubkey, (onboardingPubkeyCounts.get(ev.pubkey) || 0) + 1);
    });

    return combined.map((ev): ClaimDiag => {
      let data: any = {};
      try { data = JSON.parse(ev.content); } catch {}
      const addr = ev.tags.find((t: string[]) => t[0] === 'bch_address')?.[1] || data.bchAddress || '';
      const addrNormalized = normalizeBCHAddress(addr);
      const addrValid = isValidBCHAddress(addr);
      const isOnboarding = onboardingIds.has(ev.id);
      const issues: string[] = [];
      let riskLevel: 'ok' | 'warn' | 'danger' = 'ok';

      // Check fraud blocklist
      const isFraudBlocked = fraudBlocklist.isAddressBlocked(addr) || fraudBlocklist.isPubkeyBlocked(ev.pubkey);
      if (isFraudBlocked) {
        issues.push('Fraud blocklisted');
        riskLevel = 'danger';
      }

      // Sybil detection: shared address across multiple pubkeys
      const addrKey = addrNormalized || addr;
      if (addrKey && sharedAddressSet.has(addrKey)) {
        issues.push('Shared address (possible sybil)');
        riskLevel = 'danger';
      }

      // Address checks
      if (!addr) {
        issues.push('No BCH address provided');
        riskLevel = 'danger';
      } else if (!addrValid) {
        issues.push('Invalid BCH address format');
        riskLevel = 'danger';
      }

      // Reward sanity check
      const reward = data.reward || 0;
      if (reward <= 0) {
        issues.push('Zero or negative reward');
        riskLevel = 'danger';
      }
      if (reward > 100000) {
        issues.push('Unusually large reward (>100k sats)');
        if (riskLevel !== 'danger') riskLevel = 'warn';
      }

      // Tip count check
      const tipCount = data.tipCount || data.uniqueRecipients || 0;
      if (tipCount < 3) {
        issues.push(`Low tip count (${tipCount})`);
        if (riskLevel !== 'danger') riskLevel = 'warn';
      }

      // Onboarding-specific checks
      if (isOnboarding) {
        // Duplicate onboarding claim from same pubkey
        const pubkeyCount = onboardingPubkeyCounts.get(ev.pubkey) || 0;
        if (pubkeyCount > 1) {
          issues.push(`Duplicate onboarding (${pubkeyCount}x from same npub)`);
          riskLevel = 'danger';
        }

        // Already paid this pubkey before (trying to re-claim via addressable event replacement)
        if (paidClaims.paidPubkeys.has(ev.pubkey) && !paidClaims.paidSet.has(ev.id)) {
          issues.push('Pubkey already paid (possible re-claim)');
          riskLevel = 'danger';
        }

        // Minimum-effort: exactly 3 tips
        if (tipCount === 3) {
          issues.push('Minimum-effort (exactly 3 tips)');
          if (riskLevel === 'ok') riskLevel = 'warn';
        }

        // Timestamp mismatch — event claims far more tips than reasonable
        if (tipCount >= 10 && reward <= 10000) {
          issues.push('High tips but low reward tier — possible data mismatch');
          if (riskLevel === 'ok') riskLevel = 'warn';
        }
      }

      // Account freshness — event created within 24h of first possible creation
      const accountAge = ev.created_at - (data.timestamp || ev.created_at);
      if (accountAge === 0 && tipCount <= 3) {
        issues.push('Claimed immediately with minimum tips');
        if (riskLevel !== 'danger') riskLevel = 'warn';
      }

      // For onboarding: paid if event ID matches OR pubkey was already paid
      // For loyalty: paid only by event ID (same pubkey can claim again)
      const isPaid = isOnboarding
        ? (paidClaims.paidSet.has(ev.id) || paidClaims.paidPubkeys.has(ev.pubkey))
        : paidClaims.paidSet.has(ev.id);

      return {
        eventId: ev.id,
        pubkey: ev.pubkey,
        addr,
        addrNormalized,
        addrValid,
        reward,
        tier: data.tier || 'Unknown',
        tipCount,
        createdAt: ev.created_at,
        isPaid,
        claimType: isOnboarding ? 'onboarding' : 'loyalty',
        issues,
        riskLevel,
        isFraudBlocked,
      };
    });
  }, [onboardingClaims, loyaltyClaims, onboardingIds, paidClaims.paidSet, paidClaims.paidPubkeys, sharedAddressSet, fraudBlocklist]);

  // Separate by type
  const onboardingDiags = allDiags.filter(d => onboardingClaims.some(ev => ev.id === d.eventId));
  const loyaltyDiags = allDiags.filter(d => loyaltyClaims.some(ev => ev.id === d.eventId));

  // Unpaid only
  const unpaidOnboarding = onboardingDiags.filter(d => !d.isPaid);
  const unpaidLoyalty = loyaltyDiags.filter(d => !d.isPaid);
  const allUnpaid = allDiags.filter(d => !d.isPaid);

  // Issue counts
  const dangerCount = allUnpaid.filter(d => d.riskLevel === 'danger').length;
  const warnCount = allUnpaid.filter(d => d.riskLevel === 'warn').length;

  // Duplicate address detection (across all claims)
  const addrMap = useMemo(() => {
    const m = new Map<string, ClaimDiag[]>();
    allDiags.forEach(d => {
      const key = d.addrNormalized || d.addr;
      if (!key) return;
      const arr = m.get(key) || [];
      arr.push(d);
      m.set(key, arr);
    });
    return m;
  }, [allDiags]);

  const duplicateAddrs = useMemo(() => {
    const dupes: { addr: string; claims: ClaimDiag[] }[] = [];
    addrMap.forEach((claims, addr) => {
      // Multiple different pubkeys using the same address
      const uniquePubkeys = new Set(claims.map(c => c.pubkey));
      if (uniquePubkeys.size > 1) {
        dupes.push({ addr, claims });
      }
    });
    return dupes;
  }, [addrMap]);

  // Duplicate pubkey detection (multiple claims from same user, important for onboarding)
  const pubkeyClaimCounts = useMemo(() => {
    const m = new Map<string, number>();
    onboardingDiags.forEach(d => {
      m.set(d.pubkey, (m.get(d.pubkey) || 0) + 1);
    });
    return m;
  }, [onboardingDiags]);

  const multiClaimPubkeys = useMemo(() => {
    return Array.from(pubkeyClaimCounts.entries()).filter(([, count]) => count > 1);
  }, [pubkeyClaimCounts]);

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="glass-card border-purple-500/30 w-full text-left hover:border-purple-500/50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-purple-500" />
            <span className="text-sm font-semibold">Diagnostics</span>
            {dangerCount > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-500 text-[10px] font-bold">{dangerCount} critical</span>
            )}
            {warnCount > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-500 text-[10px] font-bold">{warnCount} warnings</span>
            )}
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        </div>
      </button>
    );
  }

  return (
    <div className="glass-card border-purple-500/30 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Activity className="w-4 h-4 text-purple-500" /> Diagnostics
        </h3>
        <button onClick={() => setExpanded(false)} className="p-1 hover:bg-muted rounded">
          <ChevronUp className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 flex-wrap">
        {([
          { key: 'overview', label: 'Overview', icon: BarChart3 },
          { key: 'claims', label: 'Claims', icon: Search },
          { key: 'addresses', label: 'Addresses', icon: Wallet },
          { key: 'fraud', label: 'Fraud', icon: Ban },
          { key: 'payout', label: 'Payout', icon: Send },
        ] as const).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-purple-500 text-white'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="w-3 h-3" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <DiagOverview
          onboardingDiags={onboardingDiags}
          loyaltyDiags={loyaltyDiags}
          allUnpaid={allUnpaid}
          dangerCount={dangerCount}
          warnCount={warnCount}
          duplicateAddrs={duplicateAddrs}
          multiClaimPubkeys={multiClaimPubkeys}
          bchPrice={bchPrice}
        />
      )}
      {activeTab === 'claims' && (
        <DiagClaims diags={allDiags} bchPrice={bchPrice} payouts={paidClaims.payouts} />
      )}
      {activeTab === 'addresses' && (
        <DiagAddresses diags={allDiags} duplicateAddrs={duplicateAddrs} addrMap={addrMap} />
      )}
      {activeTab === 'fraud' && (
        <DiagFraud
          diags={allDiags}
          duplicateAddrs={duplicateAddrs}
          multiClaimPubkeys={multiClaimPubkeys}
          onboardingDiags={onboardingDiags}
          fraudBlocklist={fraudBlocklist}
        />
      )}
      {activeTab === 'payout' && (
        <DiagPayout
          allUnpaid={allUnpaid}
          bchPrice={bchPrice}
          paidClaims={paidClaims}
          fraudBlocklist={fraudBlocklist}
        />
      )}
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────
function DiagOverview({
  onboardingDiags,
  loyaltyDiags,
  allUnpaid,
  dangerCount,
  warnCount,
  duplicateAddrs,
  multiClaimPubkeys,
  bchPrice,
}: {
  onboardingDiags: ClaimDiag[];
  loyaltyDiags: ClaimDiag[];
  allUnpaid: ClaimDiag[];
  dangerCount: number;
  warnCount: number;
  duplicateAddrs: { addr: string; claims: ClaimDiag[] }[];
  multiClaimPubkeys: [string, number][];
  bchPrice: number | null;
}) {
  const totalUnpaidSats = allUnpaid.reduce((s, d) => s + d.reward, 0);
  const validUnpaid = allUnpaid.filter(d => d.addrValid && d.riskLevel !== 'danger');
  const validUnpaidSats = validUnpaid.reduce((s, d) => s + d.reward, 0);
  const invalidCount = allUnpaid.filter(d => !d.addrValid).length;

  // Unique pubkeys
  const uniquePubkeys = new Set(onboardingDiags.map(d => d.pubkey)).size;
  const uniqueLoyaltyPubkeys = new Set(loyaltyDiags.map(d => d.pubkey)).size;

  // Per-type breakdowns
  const unpaidOnboarding = onboardingDiags.filter(d => !d.isPaid);
  const paidOnboarding = onboardingDiags.filter(d => d.isPaid);
  const unpaidLoyalty = loyaltyDiags.filter(d => !d.isPaid);
  const paidLoyalty = loyaltyDiags.filter(d => d.isPaid);

  const unpaidOnboardingSats = unpaidOnboarding.reduce((s, d) => s + d.reward, 0);
  const paidOnboardingSats = paidOnboarding.reduce((s, d) => s + d.reward, 0);
  const unpaidLoyaltySats = unpaidLoyalty.reduce((s, d) => s + d.reward, 0);
  const paidLoyaltySats = paidLoyalty.reduce((s, d) => s + d.reward, 0);

  // Onboarding-specific issue counts
  const onboardingDangers = unpaidOnboarding.filter(d => d.riskLevel === 'danger').length;
  const onboardingWarns = unpaidOnboarding.filter(d => d.riskLevel === 'warn').length;
  const onboardingInvalid = unpaidOnboarding.filter(d => !d.addrValid).length;

  return (
    <div className="space-y-3">
      {/* Status badges */}
      {(dangerCount > 0 || warnCount > 0 || duplicateAddrs.length > 0 || multiClaimPubkeys.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {dangerCount > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-red-500/10 border border-red-500/20">
              <FileWarning className="w-3 h-3 text-red-500" />
              <span className="text-[10px] font-bold text-red-500">{dangerCount} critical</span>
            </div>
          )}
          {warnCount > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <AlertTriangle className="w-3 h-3 text-amber-500" />
              <span className="text-[10px] font-bold text-amber-500">{warnCount} warning{warnCount !== 1 ? 's' : ''}</span>
            </div>
          )}
          {duplicateAddrs.length > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-orange-500/10 border border-orange-500/20">
              <Users className="w-3 h-3 text-orange-500" />
              <span className="text-[10px] font-bold text-orange-500">{duplicateAddrs.length} shared addr{duplicateAddrs.length !== 1 ? 's' : ''}</span>
            </div>
          )}
          {multiClaimPubkeys.length > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-orange-500/10 border border-orange-500/20">
              <Ban className="w-3 h-3 text-orange-500" />
              <span className="text-[10px] font-bold text-orange-500">{multiClaimPubkeys.length} multi-claim{multiClaimPubkeys.length !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
      )}

      {/* ── Onboarding Claims (Free BCH) ─────────────────────────── */}
      <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Droplets className="w-4 h-4 text-blue-500" />
          <span className="text-xs font-bold">Onboarding Claims (Free BCH)</span>
          <span className="text-[9px] bg-blue-500/10 text-blue-500 px-1.5 py-0.5 rounded-full font-medium">one-time</span>
        </div>

        <div className="grid grid-cols-3 gap-1.5">
          <div className="rounded bg-background/50 p-2 text-center">
            <p className="text-sm font-bold">{onboardingDiags.length}</p>
            <p className="text-[9px] text-muted-foreground">Total</p>
          </div>
          <div className="rounded bg-background/50 p-2 text-center">
            <p className="text-sm font-bold text-emerald-500">{paidOnboarding.length}</p>
            <p className="text-[9px] text-muted-foreground">Paid</p>
          </div>
          <div className="rounded bg-background/50 p-2 text-center">
            <p className={`text-sm font-bold ${unpaidOnboarding.length > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{unpaidOnboarding.length}</p>
            <p className="text-[9px] text-muted-foreground">Pending</p>
          </div>
        </div>

        <div className="space-y-0.5 text-[10px]">
          <div className="flex justify-between text-muted-foreground">
            <span>{uniquePubkeys} unique users</span>
            <span>Paid: {satsToUSD(paidOnboardingSats, bchPrice) || `${paidOnboardingSats.toLocaleString()}s`}</span>
          </div>
          {unpaidOnboarding.length > 0 && (
            <div className="flex justify-between font-medium">
              <span className="text-red-500">
                {unpaidOnboarding.length} pending
                {onboardingDangers > 0 && <span className="ml-1">({onboardingDangers} flagged)</span>}
              </span>
              <span className="text-red-500">{satsToUSD(unpaidOnboardingSats, bchPrice) || `${unpaidOnboardingSats.toLocaleString()} sats`}</span>
            </div>
          )}
          {onboardingInvalid > 0 && (
            <p className="text-red-500 flex items-center gap-1"><X className="w-3 h-3" /> {onboardingInvalid} with invalid address</p>
          )}
          {multiClaimPubkeys.length > 0 && (
            <p className="text-orange-500 flex items-center gap-1"><Ban className="w-3 h-3" /> {multiClaimPubkeys.length} pubkey{multiClaimPubkeys.length !== 1 ? 's' : ''} with duplicate claims</p>
          )}
          {unpaidOnboarding.length === 0 && (
            <p className="text-emerald-500 flex items-center gap-1"><Check className="w-3 h-3" /> All caught up!</p>
          )}
        </div>
      </div>

      {/* ── Loyalty Claims ───────────────────────────────────────── */}
      <div className="rounded-lg border border-pink-500/20 bg-pink-500/5 p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Heart className="w-4 h-4 text-pink-500" />
          <span className="text-xs font-bold">Loyalty Claims</span>
          <span className="text-[9px] bg-pink-500/10 text-pink-500 px-1.5 py-0.5 rounded-full font-medium">recurring</span>
        </div>

        <div className="grid grid-cols-3 gap-1.5">
          <div className="rounded bg-background/50 p-2 text-center">
            <p className="text-sm font-bold">{loyaltyDiags.length}</p>
            <p className="text-[9px] text-muted-foreground">Total</p>
          </div>
          <div className="rounded bg-background/50 p-2 text-center">
            <p className="text-sm font-bold text-emerald-500">{paidLoyalty.length}</p>
            <p className="text-[9px] text-muted-foreground">Paid</p>
          </div>
          <div className="rounded bg-background/50 p-2 text-center">
            <p className={`text-sm font-bold ${unpaidLoyalty.length > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{unpaidLoyalty.length}</p>
            <p className="text-[9px] text-muted-foreground">Pending</p>
          </div>
        </div>

        <div className="space-y-0.5 text-[10px]">
          <div className="flex justify-between text-muted-foreground">
            <span>{uniqueLoyaltyPubkeys} unique users</span>
            <span>Paid: {satsToUSD(paidLoyaltySats, bchPrice) || `${paidLoyaltySats.toLocaleString()}s`}</span>
          </div>
          {unpaidLoyalty.length > 0 && (
            <div className="flex justify-between font-medium">
              <span className="text-red-500">{unpaidLoyalty.length} pending</span>
              <span className="text-red-500">{satsToUSD(unpaidLoyaltySats, bchPrice) || `${unpaidLoyaltySats.toLocaleString()} sats`}</span>
            </div>
          )}
          {unpaidLoyalty.length === 0 && loyaltyDiags.length > 0 && (
            <p className="text-emerald-500 flex items-center gap-1"><Check className="w-3 h-3" /> All caught up!</p>
          )}
          {loyaltyDiags.length === 0 && (
            <p className="text-muted-foreground">No loyalty claims yet</p>
          )}
        </div>
      </div>

      {/* ── Combined totals ──────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-muted/50 p-2.5 text-center">
          <p className="text-lg font-bold text-red-500">{allUnpaid.length}</p>
          <p className="text-[10px] text-muted-foreground">Total pending</p>
          <p className="text-[9px] text-muted-foreground">{satsToUSD(totalUnpaidSats, bchPrice) || `${totalUnpaidSats.toLocaleString()} sats`}</p>
        </div>
        <div className="rounded-lg bg-muted/50 p-2.5 text-center">
          <p className="text-lg font-bold text-emerald-500">{validUnpaid.length}</p>
          <p className="text-[10px] text-muted-foreground">Ready to pay</p>
          <p className="text-[9px] text-muted-foreground">
            {satsToUSD(validUnpaidSats, bchPrice) || `${validUnpaidSats.toLocaleString()} sats`}
            {invalidCount > 0 && <span className="text-red-500 ml-1">({invalidCount} invalid)</span>}
          </p>
        </div>
      </div>

      {/* Quick summary */}
      <div className="rounded-lg bg-muted/30 border border-border p-2.5 space-y-1">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Quick Summary</p>
        <div className="space-y-1 text-xs text-muted-foreground">
          {dangerCount === 0 && warnCount === 0 && duplicateAddrs.length === 0 && multiClaimPubkeys.length === 0 ? (
            <p className="text-emerald-500 flex items-center gap-1"><Check className="w-3 h-3" /> All claims look clean. No issues detected.</p>
          ) : (
            <>
              {invalidCount > 0 && (
                <p className="text-red-500 flex items-center gap-1"><X className="w-3 h-3" /> {invalidCount} claim{invalidCount !== 1 ? 's' : ''} with invalid/missing BCH address</p>
              )}
              {duplicateAddrs.length > 0 && (
                <p className="text-orange-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {duplicateAddrs.length} address{duplicateAddrs.length !== 1 ? 'es' : ''} shared by multiple users (possible sybil)</p>
              )}
              {multiClaimPubkeys.length > 0 && (
                <p className="text-orange-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {multiClaimPubkeys.length} user{multiClaimPubkeys.length !== 1 ? 's' : ''} with multiple onboarding claims</p>
              )}
              {allUnpaid.filter(d => d.claimType === 'onboarding' && d.tipCount <= 3 && d.reward >= 25000).length > 0 && (
                <p className="text-amber-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {allUnpaid.filter(d => d.claimType === 'onboarding' && d.tipCount <= 3 && d.reward >= 25000).length} high-reward onboarding claim{allUnpaid.filter(d => d.claimType === 'onboarding' && d.tipCount <= 3 && d.reward >= 25000).length !== 1 ? 's' : ''} with minimal tipping</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Claims Tab — detailed claim list with issue flags ─────────────
function DiagClaims({ diags, bchPrice, payouts }: {
  diags: ClaimDiag[];
  bchPrice: number | null;
  payouts: Record<string, PayoutReceipt>;
}) {
  const [filter, setFilter] = useState<'all' | 'issues' | 'unpaid' | 'onboarding' | 'loyalty'>('issues');
  const [sortBy, setSortBy] = useState<'date' | 'reward' | 'risk'>('risk');

  const onboardingCount = diags.filter(d => d.claimType === 'onboarding').length;
  const loyaltyCount = diags.filter(d => d.claimType === 'loyalty').length;

  const filtered = useMemo(() => {
    let list = [...diags];
    if (filter === 'issues') list = list.filter(d => d.issues.length > 0);
    if (filter === 'unpaid') list = list.filter(d => !d.isPaid);
    if (filter === 'onboarding') list = list.filter(d => d.claimType === 'onboarding');
    if (filter === 'loyalty') list = list.filter(d => d.claimType === 'loyalty');

    if (sortBy === 'date') list.sort((a, b) => b.createdAt - a.createdAt);
    if (sortBy === 'reward') list.sort((a, b) => b.reward - a.reward);
    if (sortBy === 'risk') {
      const riskOrder = { danger: 0, warn: 1, ok: 2 };
      list.sort((a, b) => riskOrder[a.riskLevel] - riskOrder[b.riskLevel]);
    }
    return list;
  }, [diags, filter, sortBy]);

  return (
    <div className="space-y-2">
      <div className="flex gap-1 flex-wrap">
        {([
          { key: 'issues' as const, label: `Issues (${diags.filter(d => d.issues.length > 0).length})` },
          { key: 'unpaid' as const, label: `Unpaid (${diags.filter(d => !d.isPaid).length})` },
          { key: 'onboarding' as const, label: `Onboarding (${onboardingCount})` },
          { key: 'loyalty' as const, label: `Loyalty (${loyaltyCount})` },
          { key: 'all' as const, label: `All (${diags.length})` },
        ]).map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`px-2 py-1 rounded-lg text-[10px] font-medium ${filter === f.key ? 'bg-purple-500 text-white' : 'bg-muted text-muted-foreground'}`}>
            {f.label}
          </button>
        ))}
      </div>
      <div className="flex gap-1 items-center">
        <span className="text-[10px] text-muted-foreground">Sort:</span>
        {(['risk', 'date', 'reward'] as const).map(s => (
          <button key={s} onClick={() => setSortBy(s)}
            className={`px-2 py-1 rounded-lg text-[10px] font-medium ${sortBy === s ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
            {s}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4 flex items-center justify-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> No matching claims</p>
      ) : (
        <div className="space-y-1.5 max-h-80 overflow-y-auto">
          {filtered.map(d => (
            <DiagClaimRow key={d.eventId} diag={d} bchPrice={bchPrice} payout={payouts[d.eventId]} />
          ))}
        </div>
      )}
    </div>
  );
}

function DiagClaimRow({ diag, bchPrice, payout }: {
  diag: ClaimDiag;
  bchPrice: number | null;
  payout?: PayoutReceipt;
}) {
  const { profile } = useNostrProfile(diag.pubkey);
  const riskColors = {
    ok: 'border-border',
    warn: 'border-amber-500/30 bg-amber-500/5',
    danger: 'border-red-500/30 bg-red-500/5',
  };

  // ── Payout receipt: confirm the recorded txid actually landed on-chain ──
  // A claim marked "Paid" with a stored txid gets verified against the chain
  // on demand. "Paid but NO txid on record" is the silent-failure case that
  // stranded users before payout receipts existed — we surface it loudly.
  const [payoutConfirmed, setPayoutConfirmed] = useState<null | 'confirmed' | 'missing' | 'checking'>(null);
  const checkPayout = useCallback(async () => {
    if (!payout?.txid || !payout.address) return;
    setPayoutConfirmed('checking');
    const res = await verifyTxPaysAddress(payout.txid, payout.address).catch(() => null);
    // null = explorer unreachable (leave unknown); paid=false shouldn't happen
    // for our own broadcast but is treated as missing.
    setPayoutConfirmed(res === null ? null : res.paid ? 'confirmed' : 'missing');
  }, [payout?.txid, payout?.address]);

  // ── On-chain tip verification (operator-triggered, lazy) ──
  // Only queries relays + explorers when the operator clicks Verify, so the
  // 200-row list doesn't fan out into hundreds of background lookups.
  //
  // No `since` window: tips can be older than any fixed cutoff, and missing
  // real-but-old receipts is exactly what produced misleading "0/0" results.
  const [verifyOn, setVerifyOn] = useState(false);
  const [tipResult, setTipResult] = useState<TipVerificationSummary | null>(null);
  const [tipChecking, setTipChecking] = useState(false);
  const ranRef = useRef(false);
  const { events: claimantTips, loading: tipsLoading } = useNostrQuery({
    filters: verifyOn
      ? [{ kinds: [9735], authors: [diag.pubkey], '#t': ['bch-tip'], limit: 200 }]
      : [],
    enabled: verifyOn,
    allRelays: true,
  });

  // Wait until the query settles, THEN give streamed events a moment to finish
  // arriving before checking — the previous version ran the instant `loading`
  // flipped false, catching an empty list and caching a false "0/0". Run once.
  useEffect(() => {
    if (!verifyOn || tipsLoading || ranRef.current) return;
    const t = setTimeout(() => {
      if (ranRef.current) return;
      ranRef.current = true;
      setTipChecking(true);
      verifyClaimantTips(diag.pubkey, claimantTips)
        .then(setTipResult)
        .catch(() => setTipResult(null))
        .finally(() => setTipChecking(false));
    }, 1200);
    return () => clearTimeout(t);
  }, [verifyOn, tipsLoading, claimantTips, diag.pubkey]);

  const recheck = () => {
    ranRef.current = false;
    setTipResult(null);
    setTipChecking(false);
    setVerifyOn(false);
    // Re-enable on the next tick so the query re-subscribes fresh.
    setTimeout(() => setVerifyOn(true), 0);
  };

  const fabricated = tipResult ? looksFabricated(tipResult) : false;
  const allConfirmed = !!tipResult && tipResult.verified > 0 && tipResult.verified === tipResult.total;

  return (
    <div className={`p-2 rounded-lg border ${fabricated ? 'border-red-500/50 bg-red-500/5' : riskColors[diag.riskLevel]}`}>
      <div className="flex items-center gap-2">
        <Link to={`/profile/${diag.pubkey}`} className="shrink-0">
          {profile?.picture ? (
            <img src={profile.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center"><User className="w-3.5 h-3.5 text-primary/60" /></div>
          )}
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-medium truncate">{profile?.name || diag.pubkey.slice(0, 10)}</span>
            <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${
              diag.claimType === 'onboarding' ? 'bg-blue-500/10 text-blue-500' : 'bg-pink-500/10 text-pink-500'
            }`}>{diag.claimType === 'onboarding' ? 'Onboard' : 'Loyalty'}</span>
            <span className={`text-[9px] px-1 py-0.5 rounded font-medium ${
              diag.isPaid ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'
            }`}>{diag.isPaid ? 'Paid' : 'Pending'}</span>
            {diag.isFraudBlocked && (
              <span className="text-[9px] px-1 py-0.5 rounded font-bold bg-red-500/20 text-red-500 border border-red-500/30">BLOCKED</span>
            )}
            <span className="text-[9px] text-muted-foreground">{diag.tier} · {satsToUSD(diag.reward, bchPrice) || `${diag.reward.toLocaleString()} sats`}</span>
          </div>
          {diag.issues.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-0.5">
              {diag.issues.map((issue, i) => (
                <span key={i} className={`text-[9px] px-1 py-0.5 rounded ${
                  diag.riskLevel === 'danger' ? 'bg-red-500/10 text-red-500' : 'bg-amber-500/10 text-amber-500'
                }`}>{issue}</span>
              ))}
            </div>
          )}
        </div>
        {/* Verify tips on-chain */}
        {!diag.isPaid && (
          <div className="shrink-0">
            {!verifyOn ? (
              <button
                onClick={() => setVerifyOn(true)}
                className="flex items-center gap-1 px-1.5 py-1 rounded bg-muted text-[9px] font-medium text-muted-foreground hover:text-foreground"
                title="Look up this account's tips and confirm them against the BCH chain"
              >
                <Shield className="w-3 h-3" /> Verify tips
              </button>
            ) : tipChecking || tipsLoading || !tipResult ? (
              <span className="flex items-center gap-1 text-[9px] text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" /> Checking…
              </span>
            ) : (
              <button
                onClick={recheck}
                className={`flex items-center gap-1 px-1.5 py-1 rounded text-[9px] font-semibold ${
                  fabricated ? 'bg-red-500/15 text-red-500'
                  : allConfirmed ? 'bg-emerald-500/15 text-emerald-500'
                  : 'bg-muted text-muted-foreground'
                }`}
                title={describeTipVerification(tipResult) + ' — click to re-check'}
              >
                {fabricated ? <X className="w-3 h-3" /> : allConfirmed ? <Check className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                {tipResult.receiptsFound === 0
                  ? '0 receipts'
                  : `${tipResult.verified}/${tipResult.total} confirmed`}
              </button>
            )}
          </div>
        )}
      </div>
      {tipResult && !tipChecking && (
        <p className={`mt-1 text-[9px] flex items-start gap-1 pl-9 ${fabricated ? 'text-red-500' : 'text-muted-foreground'}`}>
          {fabricated
            ? <><AlertTriangle className="w-3 h-3 shrink-0 mt-px" /> {describeTipVerification(tipResult)} — receipts carry no on-chain txid. Strong farming signal; review before paying.</>
            : tipResult.receiptsFound === 0
              ? <><AlertTriangle className="w-3 h-3 shrink-0 mt-px text-amber-500" /> No tip receipts found on the relays. Could be old/unsynced tips — not proof of fraud. Re-check or confirm manually before deciding.</>
              : <><Activity className="w-3 h-3 shrink-0 mt-px" /> {describeTipVerification(tipResult)}. Unconfirmed tips are often split (community + personal) payments we couldn't fully trace — not necessarily fake.</>
          }
        </p>
      )}

      {/* ── Payout receipt (for paid claims) ── */}
      {diag.isPaid && (
        <div className="mt-1 pl-9">
          {payout?.txid ? (
            <div className="flex flex-wrap items-center gap-1.5 text-[9px]">
              <span className="text-muted-foreground">Paid</span>
              <span className="font-mono text-foreground">{payout.sats.toLocaleString()} sats</span>
              <span className="text-muted-foreground">→</span>
              <span className="font-mono text-muted-foreground" title={payout.address}>
                {payout.address.replace('bitcoincash:', '').slice(0, 12)}…
              </span>
              <a
                href={`https://bchexplorer.cash/tx/${payout.txid}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-0.5 text-primary hover:underline font-mono"
                title={payout.txid}
              >
                tx {payout.txid.slice(0, 8)}… <ExternalLink className="w-2.5 h-2.5" />
              </a>
              {payoutConfirmed === null ? (
                <button onClick={checkPayout} className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground hover:text-foreground font-medium">
                  Confirm on-chain
                </button>
              ) : payoutConfirmed === 'checking' ? (
                <span className="flex items-center gap-1 text-muted-foreground"><Loader2 className="w-2.5 h-2.5 animate-spin" /> Checking…</span>
              ) : payoutConfirmed === 'confirmed' ? (
                <span className="flex items-center gap-0.5 text-emerald-500 font-semibold"><Check className="w-2.5 h-2.5" /> Confirmed on-chain</span>
              ) : (
                <span className="flex items-center gap-0.5 text-red-500 font-semibold"><AlertTriangle className="w-2.5 h-2.5" /> NOT found on-chain</span>
              )}
            </div>
          ) : (
            <div className="flex items-start gap-1 text-[9px] text-amber-500">
              <AlertTriangle className="w-3 h-3 shrink-0 mt-px" />
              <span>
                Marked paid, but <strong>no payout transaction is on record</strong> — this claim was
                paid before receipt tracking, or the broadcast may have silently failed. If a user
                reports never receiving it, verify manually and re-pay if needed.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Addresses Tab — address validation & dedup ────────────────────
function DiagAddresses({
  diags,
  duplicateAddrs,
  addrMap,
}: {
  diags: ClaimDiag[];
  duplicateAddrs: { addr: string; claims: ClaimDiag[] }[];
  addrMap: Map<string, ClaimDiag[]>;
}) {
  const invalidAddrs = diags.filter(d => !d.addrValid && d.addr);
  const missingAddrs = diags.filter(d => !d.addr);

  // Top receiving addresses by total sats
  const addrBySats = useMemo(() => {
    const arr: { addr: string; totalSats: number; claimCount: number; pubkeys: string[] }[] = [];
    addrMap.forEach((claims, addr) => {
      arr.push({
        addr,
        totalSats: claims.reduce((s, c) => s + c.reward, 0),
        claimCount: claims.length,
        pubkeys: [...new Set(claims.map(c => c.pubkey))],
      });
    });
    return arr.sort((a, b) => b.totalSats - a.totalSats).slice(0, 20);
  }, [addrMap]);

  return (
    <div className="space-y-3">
      {/* Issues summary */}
      <div className="rounded-lg bg-muted/30 border border-border p-2.5 space-y-1.5">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Address Health</p>
        <div className="flex items-center gap-2 text-xs">
          {missingAddrs.length === 0 && invalidAddrs.length === 0 && duplicateAddrs.length === 0 ? (
            <span className="text-emerald-500 flex items-center gap-1"><Check className="w-3 h-3" /> All addresses valid</span>
          ) : (
            <div className="space-y-0.5 w-full">
              {missingAddrs.length > 0 && (
                <p className="text-red-500 flex items-center gap-1"><X className="w-3 h-3" /> {missingAddrs.length} claim{missingAddrs.length !== 1 ? 's' : ''} missing address</p>
              )}
              {invalidAddrs.length > 0 && (
                <p className="text-red-500 flex items-center gap-1"><X className="w-3 h-3" /> {invalidAddrs.length} claim{invalidAddrs.length !== 1 ? 's' : ''} with invalid address format</p>
              )}
              {duplicateAddrs.length > 0 && (
                <p className="text-orange-500 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {duplicateAddrs.length} address{duplicateAddrs.length !== 1 ? 'es' : ''} used by multiple pubkeys</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Duplicate addresses — sybil detection */}
      {duplicateAddrs.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold text-orange-500 uppercase tracking-wider flex items-center gap-1">
            <Users className="w-3 h-3" /> Shared Addresses ({duplicateAddrs.length})
          </p>
          {duplicateAddrs.map(dup => (
            <div key={dup.addr} className="rounded-lg bg-orange-500/5 border border-orange-500/20 p-2 space-y-1">
              <p className="text-[10px] font-mono text-orange-500">{dup.addr.replace('bitcoincash:', '').slice(0, 20)}...</p>
              <div className="flex flex-wrap gap-1">
                {dup.claims.map(c => (
                  <DupClaimBadge key={c.eventId} claim={c} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Invalid addresses */}
      {invalidAddrs.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-semibold text-red-500 uppercase tracking-wider flex items-center gap-1">
            <X className="w-3 h-3" /> Invalid Addresses ({invalidAddrs.length})
          </p>
          {invalidAddrs.map(d => (
            <div key={d.eventId} className="flex items-center gap-2 text-[10px] text-red-500 bg-red-500/5 rounded-lg px-2 py-1.5 border border-red-500/10">
              <span className="font-mono truncate flex-1">{d.addr || '(empty)'}</span>
              <span className="text-muted-foreground shrink-0">{d.pubkey.slice(0, 8)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Top addresses by sats */}
      <div className="space-y-1.5">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Top Addresses by Total Sats</p>
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {addrBySats.slice(0, 10).map(a => (
            <div key={a.addr} className="flex items-center justify-between text-[10px] px-2 py-1 rounded bg-muted/30">
              <span className="font-mono text-muted-foreground truncate flex-1 mr-2">{a.addr.replace('bitcoincash:', '').slice(0, 16)}...</span>
              <div className="text-right shrink-0">
                <span className="font-bold">{a.totalSats.toLocaleString()} sats</span>
                <span className="text-muted-foreground ml-1.5">{a.claimCount} claim{a.claimCount !== 1 ? 's' : ''}</span>
                {a.pubkeys.length > 1 && <span className="text-orange-500 ml-1">({a.pubkeys.length} users!)</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DupClaimBadge({ claim }: { claim: ClaimDiag }) {
  const { profile } = useNostrProfile(claim.pubkey);
  return (
    <Link to={`/profile/${claim.pubkey}`} className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted border border-border text-[9px] hover:border-orange-500/50">
      {profile?.picture ? (
        <img src={profile.picture} alt="" className="w-4 h-4 rounded-full object-cover" />
      ) : (
        <div className="w-4 h-4 rounded-full bg-primary/10 flex items-center justify-center"><User className="w-2.5 h-2.5 text-primary/60" /></div>
      )}
      <span className="truncate max-w-[60px]">{profile?.name || claim.pubkey.slice(0, 8)}</span>
      <span className={`${claim.claimType === 'onboarding' ? 'text-blue-500' : 'text-pink-500'}`}>
        {claim.claimType === 'onboarding' ? 'OB' : 'LY'}
      </span>
      <span className="text-muted-foreground">{claim.reward.toLocaleString()}s</span>
    </Link>
  );
}

// ── Fraud Detection Tab ───────────────────────────────────────────
function DiagFraud({
  diags,
  duplicateAddrs,
  multiClaimPubkeys,
  onboardingDiags,
  fraudBlocklist,
}: {
  diags: ClaimDiag[];
  duplicateAddrs: { addr: string; claims: ClaimDiag[] }[];
  multiClaimPubkeys: [string, number][];
  onboardingDiags: ClaimDiag[];
  fraudBlocklist: ReturnType<typeof useFraudBlocklist>;
}) {
  const [blockInput, setBlockInput] = useState('');
  const [blockNote, setBlockNote] = useState('');

  // Find claims with very low tip counts but high rewards
  const suspiciousRewards = diags.filter(d => !d.isPaid && d.tipCount <= 3 && d.reward >= 25000);

  // Find claims that happened very quickly (multiple claims within 1 hour — different users, possible sybil)
  const rapidClaims = useMemo(() => {
    const sorted = [...onboardingDiags].sort((a, b) => a.createdAt - b.createdAt);
    const clusters: ClaimDiag[][] = [];
    let currentCluster: ClaimDiag[] = [];

    for (let i = 0; i < sorted.length; i++) {
      if (currentCluster.length === 0) {
        currentCluster.push(sorted[i]);
        continue;
      }
      // If within 30 minutes of previous claim
      if (sorted[i].createdAt - currentCluster[currentCluster.length - 1].createdAt < 1800) {
        currentCluster.push(sorted[i]);
      } else {
        if (currentCluster.length >= 3) clusters.push(currentCluster);
        currentCluster = [sorted[i]];
      }
    }
    if (currentCluster.length >= 3) clusters.push(currentCluster);

    return clusters;
  }, [onboardingDiags]);

  // Minimum-effort claims: exactly 3 tips, lowest tier
  const minEffortClaims = onboardingDiags.filter(d => !d.isPaid && d.tipCount === 3);

  // Count fraud-blocked claims
  const fraudBlockedClaims = diags.filter(d => d.isFraudBlocked && !d.isPaid);

  const handleBlockAddress = () => {
    const trimmed = blockInput.trim();
    if (!trimmed) return;
    // Check if it looks like a pubkey (64-char hex)
    if (/^[0-9a-f]{64}$/i.test(trimmed)) {
      fraudBlocklist.blockPubkey(trimmed, blockNote || undefined);
    } else {
      fraudBlocklist.blockAddress(trimmed, blockNote || undefined);
    }
    setBlockInput('');
    setBlockNote('');
  };

  // Quick-block all shared addresses
  const handleBlockAllShared = () => {
    duplicateAddrs.forEach(dup => {
      fraudBlocklist.blockAddress(dup.addr, `Sybil: shared by ${new Set(dup.claims.map(c => c.pubkey)).size} users`);
    });
  };

  const hasAnyIssues = duplicateAddrs.length > 0 || multiClaimPubkeys.length > 0 || suspiciousRewards.length > 0 || rapidClaims.length > 0 || minEffortClaims.length > 0 || fraudBlocklist.blockedAddresses.size > 0 || fraudBlocklist.blockedPubkeys.size > 0;

  return (
    <div className="space-y-3">
      {/* Fraud Blocklist Manager */}
      <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-red-500" />
          <span className="text-xs font-bold">Fraud Blocklist</span>
          <span className="text-[9px] bg-red-500/10 text-red-500 px-1.5 py-0.5 rounded-full font-medium">
            {fraudBlocklist.blockedAddresses.size + fraudBlocklist.blockedPubkeys.size} blocked
          </span>
        </div>
        <p className="text-[10px] text-muted-foreground">
          Blocked addresses and pubkeys are automatically excluded from mass payouts. Synced to Nostr (encrypted).
        </p>

        {/* Add to blocklist */}
        <div className="flex gap-1.5">
          <input
            type="text"
            value={blockInput}
            onChange={e => setBlockInput(e.target.value)}
            placeholder="BCH address or pubkey hex..."
            className="flex-1 min-w-0 px-2 py-1.5 rounded-lg bg-background border border-border text-[10px] font-mono placeholder:text-muted-foreground/50"
          />
          <button
            onClick={handleBlockAddress}
            disabled={!blockInput.trim()}
            className="px-2.5 py-1.5 rounded-lg bg-red-500 text-white text-[10px] font-medium hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            Block
          </button>
        </div>
        <input
          type="text"
          value={blockNote}
          onChange={e => setBlockNote(e.target.value)}
          placeholder="Reason (optional)..."
          className="w-full px-2 py-1 rounded-lg bg-background border border-border text-[10px] placeholder:text-muted-foreground/50"
        />

        {/* Quick actions */}
        {duplicateAddrs.length > 0 && (
          <button
            onClick={handleBlockAllShared}
            className="flex items-center gap-1 px-2 py-1 rounded-lg bg-orange-500/10 text-orange-500 text-[10px] font-medium hover:bg-orange-500/20 border border-orange-500/20"
          >
            <Ban className="w-3 h-3" />
            Block all {duplicateAddrs.length} shared address{duplicateAddrs.length !== 1 ? 'es' : ''}
          </button>
        )}

        {/* Current blocked items */}
        {(fraudBlocklist.blockedAddresses.size > 0 || fraudBlocklist.blockedPubkeys.size > 0) && (
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {Array.from(fraudBlocklist.blockedAddresses).map(addr => (
              <div key={addr} className="flex items-center gap-1.5 text-[9px] bg-red-500/10 rounded-lg px-2 py-1 border border-red-500/10">
                <Wallet className="w-3 h-3 text-red-500 shrink-0" />
                <span className="font-mono text-red-500 truncate flex-1">{addr.replace('bitcoincash:', '').slice(0, 20)}...</span>
                {fraudBlocklist.getNote(addr) && (
                  <span className="text-muted-foreground truncate max-w-[80px]">{fraudBlocklist.getNote(addr)}</span>
                )}
                <button
                  onClick={() => fraudBlocklist.unblockAddress(addr)}
                  className="text-muted-foreground hover:text-red-500 shrink-0"
                  title="Unblock"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {Array.from(fraudBlocklist.blockedPubkeys).map(pk => (
              <div key={pk} className="flex items-center gap-1.5 text-[9px] bg-red-500/10 rounded-lg px-2 py-1 border border-red-500/10">
                <User className="w-3 h-3 text-red-500 shrink-0" />
                <span className="font-mono text-red-500 truncate flex-1">{pk.slice(0, 16)}...</span>
                {fraudBlocklist.getNote(pk) && (
                  <span className="text-muted-foreground truncate max-w-[80px]">{fraudBlocklist.getNote(pk)}</span>
                )}
                <button
                  onClick={() => fraudBlocklist.unblockPubkey(pk)}
                  className="text-muted-foreground hover:text-red-500 shrink-0"
                  title="Unblock"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Impact summary */}
        {fraudBlockedClaims.length > 0 && (
          <div className="rounded bg-red-500/10 px-2 py-1.5 text-[10px] text-red-500 flex items-center gap-1.5">
            <Shield className="w-3 h-3 shrink-0" />
            <span>{fraudBlockedClaims.length} pending claim{fraudBlockedClaims.length !== 1 ? 's' : ''} blocked ({fraudBlockedClaims.reduce((s, d) => s + d.reward, 0).toLocaleString()} sats saved)</span>
          </div>
        )}
      </div>

      {!hasAnyIssues ? (
        <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-4 text-center">
          <Check className="w-8 h-8 text-emerald-500 mx-auto mb-1" />
          <p className="text-sm font-bold text-emerald-500">No Fraud Signals Detected</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">All claims appear legitimate</p>
        </div>
      ) : (
        <>
          {/* Sybil: shared addresses */}
          {duplicateAddrs.length > 0 && (
            <FraudSection
              icon={Users}
              color="orange"
              title="Shared Addresses (Possible Sybil)"
              description={`${duplicateAddrs.length} BCH address${duplicateAddrs.length !== 1 ? 'es are' : ' is'} used by multiple users. These are automatically flagged as danger and excluded from payouts.`}
              severity="high"
            >
              <div className="space-y-1.5">
                {duplicateAddrs.map(dup => {
                  const isBlocked = fraudBlocklist.isAddressBlocked(dup.addr);
                  return (
                    <div key={dup.addr} className="text-[10px] bg-orange-500/5 rounded-lg p-2 border border-orange-500/10 space-y-1">
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-orange-500 truncate">{dup.addr.replace('bitcoincash:', '').slice(0, 20)}...</span>
                        <span className="text-muted-foreground">({dup.claims.length} claims, {new Set(dup.claims.map(c => c.pubkey)).size} users)</span>
                        {isBlocked ? (
                          <span className="px-1 py-0.5 rounded bg-red-500/10 text-red-500 text-[8px] font-bold shrink-0">BLOCKED</span>
                        ) : (
                          <button
                            onClick={() => fraudBlocklist.blockAddress(dup.addr, `Sybil: ${new Set(dup.claims.map(c => c.pubkey)).size} users`)}
                            className="px-1.5 py-0.5 rounded bg-red-500/10 text-red-500 text-[8px] font-medium hover:bg-red-500/20 shrink-0"
                          >
                            Block
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {dup.claims.map(c => (
                          <DupClaimBadge key={c.eventId} claim={c} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </FraudSection>
          )}

          {/* Multi-claim pubkeys */}
          {multiClaimPubkeys.length > 0 && (
            <FraudSection
              icon={Ban}
              color="red"
              title="Multiple Onboarding Claims"
              description={`${multiClaimPubkeys.length} pubkey${multiClaimPubkeys.length !== 1 ? 's have' : ' has'} submitted multiple onboarding claims. The faucet is one-per-account.`}
              severity="high"
            >
              <div className="space-y-1">
                {multiClaimPubkeys.map(([pk, count]) => (
                  <div key={pk} className="flex items-center gap-2">
                    <MultiClaimRow pubkey={pk} count={count} />
                    {!fraudBlocklist.isPubkeyBlocked(pk) ? (
                      <button
                        onClick={() => fraudBlocklist.blockPubkey(pk, `Multi-claim: ${count}x onboarding`)}
                        className="px-1.5 py-0.5 rounded bg-red-500/10 text-red-500 text-[8px] font-medium hover:bg-red-500/20 shrink-0"
                      >
                        Block
                      </button>
                    ) : (
                      <span className="px-1 py-0.5 rounded bg-red-500/10 text-red-500 text-[8px] font-bold shrink-0">BLOCKED</span>
                    )}
                  </div>
                ))}
              </div>
            </FraudSection>
          )}

          {/* Rapid claiming clusters */}
          {rapidClaims.length > 0 && (
            <FraudSection
              icon={Zap}
              color="amber"
              title="Rapid Claim Clusters"
              description={`${rapidClaims.length} cluster${rapidClaims.length !== 1 ? 's' : ''} of 3+ claims within 30 minutes. May indicate coordinated claiming.`}
              severity="medium"
            >
              <div className="space-y-1.5">
                {rapidClaims.map((cluster, i) => (
                  <div key={i} className="text-[10px] bg-amber-500/5 rounded-lg p-2 border border-amber-500/10">
                    <p className="text-amber-500 font-medium">{cluster.length} claims within {Math.ceil((cluster[cluster.length - 1].createdAt - cluster[0].createdAt) / 60)} minutes</p>
                    <p className="text-muted-foreground mt-0.5">
                      {timeAgo(cluster[0].createdAt)} — {cluster.map(c => c.pubkey.slice(0, 6)).join(', ')}
                    </p>
                  </div>
                ))}
              </div>
            </FraudSection>
          )}

          {/* Suspicious high rewards with low activity */}
          {suspiciousRewards.length > 0 && (
            <FraudSection
              icon={TrendingUp}
              color="amber"
              title="High Reward / Low Activity"
              description={`${suspiciousRewards.length} unpaid claim${suspiciousRewards.length !== 1 ? 's' : ''} with 3 or fewer tips but reward >= 25k sats. May have inflated tip counts.`}
              severity="medium"
            >
              <div className="space-y-1">
                {suspiciousRewards.map(d => (
                  <div key={d.eventId} className="flex items-center justify-between text-[10px] px-2 py-1 rounded bg-muted/30">
                    <span className="text-muted-foreground">{d.pubkey.slice(0, 10)} · {d.tipCount} tips</span>
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold text-amber-500">{d.reward.toLocaleString()} sats</span>
                      {!fraudBlocklist.isPubkeyBlocked(d.pubkey) && !fraudBlocklist.isAddressBlocked(d.addr) ? (
                        <button
                          onClick={() => fraudBlocklist.blockPubkey(d.pubkey, 'High reward / low activity')}
                          className="px-1 py-0.5 rounded bg-red-500/10 text-red-500 text-[8px] font-medium hover:bg-red-500/20"
                        >
                          Block
                        </button>
                      ) : (
                        <span className="px-1 py-0.5 rounded bg-red-500/10 text-red-500 text-[8px] font-bold">BLOCKED</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </FraudSection>
          )}

          {/* Minimum-effort claims */}
          {minEffortClaims.length > 0 && (
            <FraudSection
              icon={AlertTriangle}
              color="muted"
              title="Minimum-Effort Claims"
              description={`${minEffortClaims.length} unpaid claim${minEffortClaims.length !== 1 ? 's' : ''} with exactly 3 tips (the minimum required). Not necessarily fraudulent, but worth noting.`}
              severity="low"
            >
              <p className="text-[10px] text-muted-foreground">{minEffortClaims.length} claims at exactly the minimum threshold.</p>
            </FraudSection>
          )}
        </>
      )}
    </div>
  );
}

function MultiClaimRow({ pubkey, count }: { pubkey: string; count: number }) {
  const { profile } = useNostrProfile(pubkey);
  return (
    <Link to={`/profile/${pubkey}`} className="flex items-center gap-2 text-[10px] hover:bg-muted/30 rounded p-1">
      {profile?.picture ? (
        <img src={profile.picture} alt="" className="w-5 h-5 rounded-full object-cover" />
      ) : (
        <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center"><User className="w-3 h-3 text-primary/60" /></div>
      )}
      <span className="font-medium">{profile?.name || pubkey.slice(0, 10)}</span>
      <span className="text-red-500 font-bold">{count}x claims</span>
    </Link>
  );
}

function FraudSection({
  icon: Icon,
  color,
  title,
  description,
  severity,
  children,
}: {
  icon: any;
  color: string;
  title: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(severity === 'high');
  const severityBadge = {
    high: 'bg-red-500/10 text-red-500 border-red-500/20',
    medium: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    low: 'bg-muted text-muted-foreground border-border',
  };

  return (
    <div className={`rounded-lg border p-2.5 space-y-1.5 ${
      severity === 'high' ? 'border-red-500/20 bg-red-500/5' :
      severity === 'medium' ? 'border-amber-500/20 bg-amber-500/5' :
      'border-border bg-muted/30'
    }`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 text-left">
        <Icon className={`w-3.5 h-3.5 shrink-0 ${
          color === 'red' ? 'text-red-500' :
          color === 'orange' ? 'text-orange-500' :
          color === 'amber' ? 'text-amber-500' :
          'text-muted-foreground'
        }`} />
        <span className="text-xs font-semibold flex-1">{title}</span>
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${severityBadge[severity]}`}>
          {severity}
        </span>
        {open ? <ChevronUp className="w-3 h-3 text-muted-foreground shrink-0" /> : <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />}
      </button>
      {open && (
        <>
          <p className="text-[10px] text-muted-foreground">{description}</p>
          {children}
        </>
      )}
    </div>
  );
}

// ── Payout Tab — pre-flight diagnostics for mass payout ───────────
function DiagPayout({
  allUnpaid,
  bchPrice,
  paidClaims,
  fraudBlocklist,
}: {
  allUnpaid: ClaimDiag[];
  bchPrice: number | null;
  paidClaims: ReturnType<typeof usePaidClaims>;
  fraudBlocklist: ReturnType<typeof useFraudBlocklist>;
}) {
  const tipWallet = useTipWallet();
  const [walletIniting, setWalletIniting] = useState(false);
  const [batchPayState, setBatchPayState] = useState<'idle' | 'confirm' | 'sending' | 'success' | 'error'>('idle');
  const [batchTxId, setBatchTxId] = useState<string | null>(null);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [batchPaidCount, setBatchPaidCount] = useState(0);
  const [excludeDanger, setExcludeDanger] = useState(true);
  const [payScope, setPayScope] = useState<'all' | 'onboarding' | 'loyalty'>('all');

  // Filter by pay scope first
  const scopedUnpaid = payScope === 'all' ? allUnpaid
    : payScope === 'onboarding' ? allUnpaid.filter(d => d.claimType === 'onboarding')
    : allUnpaid.filter(d => d.claimType === 'loyalty');

  const unpaidOnboardingCount = allUnpaid.filter(d => d.claimType === 'onboarding').length;
  const unpaidLoyaltyCount = allUnpaid.filter(d => d.claimType === 'loyalty').length;

  // Fraud-blocked claims (always excluded regardless of excludeDanger toggle)
  const fraudBlockedClaims = scopedUnpaid.filter(d => d.isFraudBlocked);
  const fraudBlockedSats = fraudBlockedClaims.reduce((s, d) => s + d.reward, 0);

  // Separate valid from invalid — fraud-blocked are always excluded
  const validClaims = scopedUnpaid.filter(d => d.addrValid && !d.isFraudBlocked && (!excludeDanger || d.riskLevel !== 'danger'));
  const invalidClaims = scopedUnpaid.filter(d => !d.addrValid);
  const dangerClaims = scopedUnpaid.filter(d => d.riskLevel === 'danger' && d.addrValid && !d.isFraudBlocked);
  const warnClaims = validClaims.filter(d => d.riskLevel === 'warn');

  const totalValidSats = validClaims.reduce((s, d) => s + d.reward, 0);
  const totalInvalidSats = invalidClaims.reduce((s, d) => s + d.reward, 0);

  // Build batch outputs (deduplicated: combine multiple claims to same address)
  const batchOutputs = useMemo(() => {
    const addrTotals = new Map<string, { addr: string; sats: number; claims: ClaimDiag[] }>();
    validClaims.forEach(d => {
      const addr = d.addrNormalized || d.addr;
      if (!addr.startsWith('bitcoincash:')) return;
      const existing = addrTotals.get(addr);
      if (existing) {
        existing.sats += d.reward;
        existing.claims.push(d);
      } else {
        addrTotals.set(addr, { addr, sats: d.reward, claims: [d] });
      }
    });
    return Array.from(addrTotals.values()).filter(o => o.sats > 0);
  }, [validClaims]);

  const batchTotalSats = batchOutputs.reduce((s, o) => s + o.sats, 0);
  const walletBalance = Number(tipWallet.balanceSats);
  const estimatedFee = Math.ceil(batchOutputs.length * 34 + 148 + 10); // Rough BCH tx fee estimate
  const canAffordBatch = walletBalance >= batchTotalSats + estimatedFee;

  // BCH has ~max ~2500 outputs per tx. Warn if close
  const needsSplit = batchOutputs.length > 200;
  const splitBatches = useMemo(() => {
    if (!needsSplit) return [batchOutputs];
    const batches: typeof batchOutputs[] = [];
    for (let i = 0; i < batchOutputs.length; i += 200) {
      batches.push(batchOutputs.slice(i, i + 200));
    }
    return batches;
  }, [batchOutputs, needsSplit]);

  const handleInitWallet = async () => {
    setWalletIniting(true);
    try { await tipWallet.init(); } catch {}
    setWalletIniting(false);
  };

  const handleBatchPay = async () => {
    if (batchOutputs.length === 0 || !tipWallet.ready) return;
    setBatchPayState('sending');
    setBatchError(null);

    try {
      const outputs = batchOutputs.map(o => ({
        cashaddr: o.addr,
        value: BigInt(o.sats),
      }));

      const txId = await tipWallet.sendMultiOutput(outputs);

      // Guard against a silent failure: if the wallet returned no txid, the
      // broadcast did NOT confirm — do NOT record these as paid, or we'd repeat
      // exactly the "marked paid but nothing on-chain" bug that stranded users.
      if (!txId || !/^[0-9a-f]{64}$/i.test(txId)) {
        throw new Error('No transaction id returned — payout not confirmed. Nothing was marked paid.');
      }

      setBatchTxId(txId);
      setBatchPaidCount(validClaims.length);
      setBatchPayState('success');

      // Map each claim to the exact address + amount it was paid at, so every
      // claim carries an on-chain payout RECEIPT (txid + address). This is what
      // lets the operator later prove a payout landed — and to WHICH address,
      // which matters when a user changed their BCH address after claiming.
      const addrForOutput = new Map<string, { addr: string; sats: number }>();
      for (const o of batchOutputs) {
        for (const c of o.claims) addrForOutput.set(c.eventId, { addr: o.addr, sats: o.sats });
      }
      const paidAt = Math.floor(Date.now() / 1000);
      paidClaims.markAllPaid(validClaims.map(d => {
        const o = addrForOutput.get(d.eventId);
        return {
          eventId: d.eventId,
          pubkey: d.pubkey,
          receipt: o ? { txid: txId, address: o.addr, sats: o.sats, paidAt } : undefined,
        };
      }));
      tipWallet.refreshBalance();
    } catch (e) {
      setBatchError(e instanceof Error ? e.message : 'Batch payment failed');
      setBatchPayState('error');
    }
  };

  // Copy Cashonize-format batch (addr, sats per line)
  const [copied, setCopied] = useState(false);
  const handleCopyBatch = () => {
    const lines = batchOutputs.map(o => `${o.addr}, ${o.sats}`);
    navigator.clipboard.writeText(lines.join('\n')).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-3">
      {/* Scope selector */}
      <div className="flex gap-1 flex-wrap">
        {([
          { key: 'all' as const, label: `All (${allUnpaid.length})`, icon: Send },
          { key: 'onboarding' as const, label: `Onboarding (${unpaidOnboardingCount})`, icon: Droplets },
          { key: 'loyalty' as const, label: `Loyalty (${unpaidLoyaltyCount})`, icon: Heart },
        ]).map(s => (
          <button key={s.key} onClick={() => { setPayScope(s.key); setBatchPayState('idle'); }}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-colors ${
              payScope === s.key
                ? s.key === 'onboarding' ? 'bg-blue-500 text-white'
                  : s.key === 'loyalty' ? 'bg-pink-500 text-white'
                  : 'bg-purple-500 text-white'
                : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}>
            <s.icon className="w-3 h-3" />
            {s.label}
          </button>
        ))}
      </div>

      {/* Pre-flight checklist */}
      <div className="rounded-lg bg-muted/30 border border-border p-2.5 space-y-2">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          Pre-Flight Check {payScope !== 'all' && <span className="normal-case">({payScope})</span>}
        </p>

        <div className="space-y-1.5">
          {/* Valid claims */}
          <div className="flex items-center gap-2">
            {validClaims.length > 0 ? (
              <div className="w-4 h-4 rounded-full bg-emerald-500/10 flex items-center justify-center"><Check className="w-2.5 h-2.5 text-emerald-500" /></div>
            ) : (
              <div className="w-4 h-4 rounded-full bg-muted flex items-center justify-center"><X className="w-2.5 h-2.5 text-muted-foreground" /></div>
            )}
            <span className="text-xs text-muted-foreground">
              {validClaims.length} valid claim{validClaims.length !== 1 ? 's' : ''} ready ({batchOutputs.length} unique address{batchOutputs.length !== 1 ? 'es' : ''})
            </span>
          </div>

          {/* Invalid claims */}
          <div className="flex items-center gap-2">
            {invalidClaims.length === 0 ? (
              <div className="w-4 h-4 rounded-full bg-emerald-500/10 flex items-center justify-center"><Check className="w-2.5 h-2.5 text-emerald-500" /></div>
            ) : (
              <div className="w-4 h-4 rounded-full bg-red-500/10 flex items-center justify-center"><X className="w-2.5 h-2.5 text-red-500" /></div>
            )}
            <span className={`text-xs ${invalidClaims.length > 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
              {invalidClaims.length === 0 ? 'All addresses valid' : `${invalidClaims.length} claim${invalidClaims.length !== 1 ? 's' : ''} with invalid address (${satsToUSD(totalInvalidSats, bchPrice) || `${totalInvalidSats.toLocaleString()} sats`} excluded)`}
            </span>
          </div>

          {/* Fraud blocklist */}
          {fraudBlockedClaims.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-red-500/10 flex items-center justify-center"><Shield className="w-2.5 h-2.5 text-red-500" /></div>
              <span className="text-xs text-red-500">
                {fraudBlockedClaims.length} fraud-blocked claim{fraudBlockedClaims.length !== 1 ? 's' : ''} excluded ({satsToUSD(fraudBlockedSats, bchPrice) || `${fraudBlockedSats.toLocaleString()} sats`} saved)
              </span>
            </div>
          )}
          {fraudBlockedClaims.length === 0 && (fraudBlocklist.blockedAddresses.size > 0 || fraudBlocklist.blockedPubkeys.size > 0) && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-emerald-500/10 flex items-center justify-center"><Shield className="w-2.5 h-2.5 text-emerald-500" /></div>
              <span className="text-xs text-muted-foreground">
                Fraud filter active ({fraudBlocklist.blockedAddresses.size + fraudBlocklist.blockedPubkeys.size} blocked) — no matches in pending claims
              </span>
            </div>
          )}

          {/* Danger claims excluded */}
          {dangerClaims.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-amber-500/10 flex items-center justify-center"><AlertTriangle className="w-2.5 h-2.5 text-amber-500" /></div>
              <span className="text-xs text-amber-500">
                {dangerClaims.length} flagged claim{dangerClaims.length !== 1 ? 's' : ''} {excludeDanger ? 'excluded' : 'included'}
              </span>
              <button
                onClick={() => setExcludeDanger(!excludeDanger)}
                className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground hover:text-foreground"
              >
                {excludeDanger ? 'Include' : 'Exclude'}
              </button>
            </div>
          )}

          {/* Warn claims included */}
          {warnClaims.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-amber-500/10 flex items-center justify-center"><AlertTriangle className="w-2.5 h-2.5 text-amber-500" /></div>
              <span className="text-xs text-amber-500">{warnClaims.length} claim{warnClaims.length !== 1 ? 's' : ''} with warnings (included)</span>
            </div>
          )}

          {/* TX size check */}
          <div className="flex items-center gap-2">
            {!needsSplit ? (
              <div className="w-4 h-4 rounded-full bg-emerald-500/10 flex items-center justify-center"><Check className="w-2.5 h-2.5 text-emerald-500" /></div>
            ) : (
              <div className="w-4 h-4 rounded-full bg-amber-500/10 flex items-center justify-center"><AlertTriangle className="w-2.5 h-2.5 text-amber-500" /></div>
            )}
            <span className={`text-xs ${needsSplit ? 'text-amber-500' : 'text-muted-foreground'}`}>
              {needsSplit
                ? `${batchOutputs.length} outputs — will need ${splitBatches.length} transactions`
                : `${batchOutputs.length} output${batchOutputs.length !== 1 ? 's' : ''} — fits in single transaction`
              }
            </span>
          </div>

          {/* Wallet balance check */}
          <div className="flex items-center gap-2">
            {tipWallet.ready ? (
              canAffordBatch ? (
                <div className="w-4 h-4 rounded-full bg-emerald-500/10 flex items-center justify-center"><Check className="w-2.5 h-2.5 text-emerald-500" /></div>
              ) : (
                <div className="w-4 h-4 rounded-full bg-red-500/10 flex items-center justify-center"><X className="w-2.5 h-2.5 text-red-500" /></div>
              )
            ) : (
              <div className="w-4 h-4 rounded-full bg-muted flex items-center justify-center"><Clock className="w-2.5 h-2.5 text-muted-foreground" /></div>
            )}
            <span className={`text-xs ${tipWallet.ready ? (canAffordBatch ? 'text-muted-foreground' : 'text-red-500') : 'text-muted-foreground'}`}>
              {tipWallet.ready
                ? canAffordBatch
                  ? `Wallet: ${satsToUSD(walletBalance, bchPrice) || `${walletBalance.toLocaleString()} sats`} (sufficient)`
                  : `Wallet: ${satsToUSD(walletBalance, bchPrice) || `${walletBalance.toLocaleString()} sats`} — need ${satsToUSD(batchTotalSats + estimatedFee - walletBalance, bchPrice) || `${(batchTotalSats + estimatedFee - walletBalance).toLocaleString()} sats`} more`
                : 'Wallet not loaded'
              }
            </span>
          </div>
        </div>
      </div>

      {/* Cost breakdown */}
      <div className="rounded-lg bg-muted/50 border border-border p-2.5 space-y-1">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          Cost Breakdown {payScope !== 'all' && <span className="normal-case">({payScope})</span>}
        </p>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Claims to pay</span>
          <span className="font-bold">{validClaims.length}</span>
        </div>
        {/* Show per-type counts when paying all */}
        {payScope === 'all' && validClaims.length > 0 && (
          <div className="flex items-center justify-between text-[10px] text-muted-foreground pl-2">
            <span className="flex items-center gap-1">
              <Droplets className="w-2.5 h-2.5 text-blue-500" />
              Onboarding: {validClaims.filter(d => d.claimType === 'onboarding').length}
            </span>
            <span className="flex items-center gap-1">
              <Heart className="w-2.5 h-2.5 text-pink-500" />
              Loyalty: {validClaims.filter(d => d.claimType === 'loyalty').length}
            </span>
          </div>
        )}
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Unique addresses</span>
          <span className="font-bold">{batchOutputs.length}</span>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Total payout</span>
          <span className="font-bold">{satsToUSD(batchTotalSats, bchPrice) || `${batchTotalSats.toLocaleString()} sats`}</span>
        </div>
        {satsToUSD(batchTotalSats, bchPrice) && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Payout (sats)</span>
            <span className="font-mono text-muted-foreground">{batchTotalSats.toLocaleString()} sats</span>
          </div>
        )}
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Est. miner fee</span>
          <span className="font-mono text-muted-foreground">~{estimatedFee.toLocaleString()} sats</span>
        </div>
        <div className="border-t border-border pt-1 mt-1 flex items-center justify-between text-xs">
          <span className="font-semibold">Total cost</span>
          <span className="font-bold text-primary">{satsToUSD(batchTotalSats + estimatedFee, bchPrice) || `${(batchTotalSats + estimatedFee).toLocaleString()} sats`}</span>
        </div>
      </div>

      {/* Output preview */}
      {batchOutputs.length > 0 && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Output Preview ({batchOutputs.length})</p>
            <button onClick={handleCopyBatch} className="flex items-center gap-1 px-2 py-1 rounded bg-muted text-muted-foreground text-[10px] font-medium hover:text-foreground">
              {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copied ? 'Copied!' : 'Copy CSV'}
            </button>
          </div>
          <div className="space-y-0.5 max-h-40 overflow-y-auto rounded-lg bg-muted/30 border border-border p-2">
            {batchOutputs.slice(0, 50).map((o, i) => (
              <div key={o.addr} className="flex items-center justify-between text-[9px] font-mono text-muted-foreground">
                <span className="truncate flex-1 mr-2">{o.addr.replace('bitcoincash:', '').slice(0, 20)}...</span>
                <span className="shrink-0 text-foreground font-bold">{o.sats.toLocaleString()}</span>
                {o.claims.length > 1 && <span className="text-amber-500 ml-1">({o.claims.length}x)</span>}
              </div>
            ))}
            {batchOutputs.length > 50 && (
              <p className="text-[9px] text-muted-foreground text-center pt-1">...and {batchOutputs.length - 50} more</p>
            )}
          </div>
        </div>
      )}

      {/* Action buttons */}
      {batchOutputs.length > 0 && batchPayState !== 'success' && (
        <div className="space-y-2">
          {!tipWallet.ready ? (
            <button onClick={handleInitWallet} disabled={walletIniting}
              className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium flex items-center justify-center gap-1.5">
              {walletIniting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wallet className="w-3.5 h-3.5" />}
              {walletIniting ? 'Loading Wallet...' : 'Load Wallet'}
            </button>
          ) : batchPayState === 'idle' ? (
            <button onClick={() => setBatchPayState('confirm')} disabled={!canAffordBatch}
              className={`w-full py-2.5 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                canAffordBatch
                  ? 'bg-gradient-to-r from-emerald-500 to-primary text-white hover:opacity-90 active:scale-[0.98]'
                  : 'bg-muted text-muted-foreground cursor-not-allowed'
              }`}>
              <Send className="w-4 h-4" />
              {payScope === 'all' ? 'Mass Payout' : payScope === 'onboarding' ? 'Pay Onboarding' : 'Pay Loyalty'}: {validClaims.length} Claims ({satsToUSD(batchTotalSats, bchPrice) || `${batchTotalSats.toLocaleString()} sats`})
            </button>
          ) : batchPayState === 'confirm' ? (
            <div className="rounded-lg bg-card border border-border p-3 space-y-2 text-center">
              <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto">
                <AlertTriangle className="w-6 h-6 text-amber-500" />
              </div>
              <p className="text-sm font-bold">Confirm {payScope === 'all' ? 'Mass Payout' : payScope === 'onboarding' ? 'Onboarding Payout' : 'Loyalty Payout'}</p>
              <p className="text-xs text-muted-foreground">
                Send <strong className="text-foreground">{satsToUSD(batchTotalSats, bchPrice) || `${batchTotalSats.toLocaleString()} sats`}</strong> to <strong className="text-foreground">{batchOutputs.length}</strong> address{batchOutputs.length !== 1 ? 'es' : ''} ({validClaims.length} {payScope === 'all' ? '' : payScope + ' '}claims)?
              </p>
              {fraudBlockedClaims.length > 0 && (
                <p className="text-[10px] text-red-500 flex items-center justify-center gap-1">
                  <Shield className="w-3 h-3" /> {fraudBlockedClaims.length} fraud-blocked claim{fraudBlockedClaims.length !== 1 ? 's' : ''} excluded ({satsToUSD(fraudBlockedSats, bchPrice) || `${fraudBlockedSats.toLocaleString()} sats`} saved)
                </p>
              )}
              {excludeDanger && dangerClaims.length > 0 && (
                <p className="text-[10px] text-amber-500">{dangerClaims.length} flagged claim{dangerClaims.length !== 1 ? 's' : ''} excluded from this payout.</p>
              )}
              <div className="flex gap-2 justify-center pt-1">
                <button onClick={() => setBatchPayState('idle')} className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted">Cancel</button>
                <button onClick={handleBatchPay} className="px-4 py-2 rounded-lg bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 flex items-center gap-1.5">
                  <Send className="w-3.5 h-3.5" /> Send Now
                </button>
              </div>
            </div>
          ) : batchPayState === 'sending' ? (
            <div className="text-center py-3 space-y-2">
              <div className="w-10 h-10 border-3 border-primary/20 border-t-primary rounded-full animate-spin mx-auto" />
              <p className="text-sm font-bold">Sending to {batchOutputs.length} addresses...</p>
              <p className="text-xs text-muted-foreground">Broadcasting transaction. Don't close this page.</p>
            </div>
          ) : batchPayState === 'error' ? (
            <div className="text-center py-2 space-y-2">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
                <X className="w-5 h-5 text-red-500" />
              </div>
              <p className="text-sm font-bold text-red-500">Mass Payout Failed</p>
              <p className="text-xs text-muted-foreground">{batchError}</p>
              <div className="flex gap-2 justify-center pt-1">
                <button onClick={() => setBatchPayState('idle')} className="px-4 py-2 rounded-lg border border-border text-xs font-medium hover:bg-muted">Try Again</button>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Success */}
      {batchPayState === 'success' && (
        <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-3 text-center space-y-2">
          <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
            <Check className="w-6 h-6 text-emerald-500" />
          </div>
          <p className="text-sm font-bold text-emerald-500">Mass Payout Complete!</p>
          <p className="text-xs text-muted-foreground">Paid {batchPaidCount} claims to {batchOutputs.length} addresses.</p>
          {batchTxId && (
            <a href={`https://blockchair.com/bitcoin-cash/transaction/${batchTxId}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline font-mono">
              {batchTxId.slice(0, 12)}...{batchTxId.slice(-8)}
              <ExternalLink className="w-2.5 h-2.5" />
            </a>
          )}
          <button onClick={() => { setBatchPayState('idle'); setBatchTxId(null); }}
            className="block mx-auto px-4 py-1.5 rounded-lg bg-muted text-foreground text-xs font-medium hover:bg-muted/80 mt-1">
            Done
          </button>
        </div>
      )}

      {/* Nothing to pay */}
      {scopedUnpaid.length === 0 && (
        <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-4 text-center">
          <Check className="w-8 h-8 text-emerald-500 mx-auto mb-1" />
          <p className="text-sm font-bold text-emerald-500">All Caught Up!</p>
          <p className="text-[10px] text-muted-foreground">No pending {payScope !== 'all' ? payScope + ' ' : ''}claims to pay.</p>
        </div>
      )}
    </div>
  );
}

